import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import { toast } from "sonner";
import {
  Truck, Package, MapPin, Printer, ArrowClockwise, CaretDown, CaretRight, CaretUp, Phone, User,
  WhatsappLogo, Path as PathIconAlias, FilePdf, DownloadSimple, Clock, Ruler, MapTrifold,
  ArrowUp, ArrowDown, List as ListIcon, DotsSixVertical, Plus, Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/lib/api";
import NumberInput from "@/components/ui/NumberInput";

const ESTADO_COLORS = {
  sin_confirmar: "bg-amber-100 text-amber-800",
  confirmado: "bg-blue-100 text-blue-800",
  en_produccion: "bg-orange-100 text-orange-800",
  listo_para_facturar: "bg-purple-100 text-purple-800",
  terminado: "bg-emerald-100 text-emerald-800",
  reprogramar: "bg-rose-100 text-rose-800",
};
const ESTADO_LABEL = {
  sin_confirmar: "Sin confirmar",
  confirmado: "Confirmado",
  en_produccion: "En producción",
  listo_para_facturar: "Listo p/ facturar",
  terminado: "Terminado",
  reprogramar: "Reprogramar",
};

// Estados de pago (payment_status del pedido)
const PAGO_LABEL = {
  paid: "PAGADO",
  authorized: "Autorizado",
  pending: "A COBRAR",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};
const PAGO_COLORS = {
  paid: "bg-emerald-100 text-emerald-800 border border-emerald-300",
  authorized: "bg-emerald-100 text-emerald-800 border border-emerald-300",
  pending: "bg-amber-100 text-amber-900 border border-amber-400",
  cancelled: "bg-neutral-200 text-neutral-500 border border-neutral-300",
  refunded: "bg-neutral-200 text-neutral-500 border border-neutral-300",
};
// ¿La parada ya está paga? (chofer NO cobra en efectivo)
const isPagado = (p) => p?.payment_status === "paid" || p?.payment_status === "authorized";

// ¿El pedido se cobra en efectivo? Si NO se define medio de pago, se asume efectivo.
// Cualquier otro medio (transferencia, tarjeta, MP, etc.) → el chofer no cobra el monto,
// solo se muestra el nombre del medio en la hoja.
const esEfectivo = (p) => {
  if (isPagado(p)) return false; // ya está pago online → no cobra
  const m = String(p?.pago_metodo || "").toLowerCase().trim();
  if (!m) return true; // sin medio definido = efectivo por defecto
  return /(^|[^a-z])(efvo|efectivo|cash|contado)([^a-z]|$)/.test(m);
};

const fmtMoney = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 }).format(Number(n) || 0);
const todayISO = () => new Date().toISOString().slice(0, 10);

// Parseo/formateo de horarios "HH:MM"
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
};
const minToHHMM = (mins) => {
  if (mins == null || !Number.isFinite(mins)) return "—";
  const total = ((Math.round(mins) % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const DEFAULT_ORIGEN = "Lisandro de la Torre 3556, Caseros, Provincia de Buenos Aires";
const ORIGEN_STORAGE_KEY = "logistica.origen_ruta";
const ORIGEN_COORDS_STORAGE_KEY = "logistica.origen_coords";
const ROUND_TRIP_STORAGE_KEY = "logistica.round_trip";
const SALIDA_STORAGE_KEY = "logistica.horario_salida_por_chofer";
const VEHICULOS_STORAGE_KEY = "logistica.vehiculos_por_chofer_viaje";
const SPLITS_STORAGE_KEY = "logistica.splits_por_chofer";
// Cache de overrides mientras la red guarda; la fuente de verdad es el backend.
const LLEGADA_OVERRIDES_CACHE_KEY = "logistica.llegada_overrides_cache";

// Velocidad promedio urbana (km/h) para estimar tiempo de viaje.
// AMBA con tráfico: ~28-32 km/h de recorrido efectivo.
const VELOCIDAD_KMH = 30;
// Minutos que se pierden en cada parada (entrega + cobro + estacionar).
const MINUTOS_POR_PARADA = 6;
// Fallback cuando una parada no tiene coordenadas cargadas: se asume un tramo
// urbano estándar para que el ETA pueda igualmente calcularse y cascadear.
const MINUTOS_TRAMO_SIN_COORDS = 12;

// Distancia haversine (km) entre dos coordenadas
const distKm = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return 999;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Algoritmo del vecino más cercano.
// Si se pasa `startCoords = {lat, lon}` se usa como punto de partida (el depósito).
// Si no, se arranca desde la parada más al noroeste (comportamiento original).
const nearestNeighborOrder = (paradas, startCoords = null) => {
  const withCoords = paradas.filter((p) => p.direccion_lat != null && p.direccion_lon != null);
  const withoutCoords = paradas.filter((p) => p.direccion_lat == null || p.direccion_lon == null);
  if (withCoords.length === 0) return { ordered: paradas, geocoded: 0 };

  let current;
  const remaining = [...withCoords];
  const ordered = [];

  if (startCoords && startCoords.lat != null && startCoords.lon != null) {
    // Depósito: primera parada = la más cercana al origen
    let bestIdx = 0; let bestD = Infinity;
    remaining.forEach((p, i) => {
      const d = distKm({ lat: startCoords.lat, lon: startCoords.lon },
                       { lat: p.direccion_lat, lon: p.direccion_lon });
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    current = remaining.splice(bestIdx, 1)[0];
    ordered.push(current);
  } else {
    // Sin depósito: la más al noroeste (menor lon, mayor lat)
    const start = withCoords.reduce((acc, p) =>
      (!acc || (p.direccion_lon < acc.direccion_lon || (p.direccion_lon === acc.direccion_lon && p.direccion_lat > acc.direccion_lat))) ? p : acc
    , null);
    const idx = remaining.findIndex((p) => p.id === start.id);
    if (idx >= 0) remaining.splice(idx, 1);
    ordered.push(start);
    current = start;
  }

  while (remaining.length > 0) {
    let best = null; let bestD = Infinity; let bestIdx = -1;
    remaining.forEach((p, i) => {
      const d = distKm({ lat: current.direccion_lat, lon: current.direccion_lon },
                       { lat: p.direccion_lat, lon: p.direccion_lon });
      if (d < bestD) { bestD = d; best = p; bestIdx = i; }
    });
    ordered.push(best);
    current = best;
    remaining.splice(bestIdx, 1);
  }
  // Las que no tienen coord se agregan al final
  return { ordered: [...ordered, ...withoutCoords], geocoded: withCoords.length };
};

// Optimización por tiempo estimado de ruta:
// 1) Las paradas con ventana horaria (`horario_entrega_desde`/`hasta`) tienen prioridad
//    y se ordenan por su hora de inicio ascendente (respeta el compromiso al cliente).
// 2) Entre paradas sin restricción horaria, se aplica nearest-neighbor arrancando
//    desde la última parada horaria (o desde el depósito si no había ninguna).
// El objetivo es minimizar el tiempo total (viaje + espera por horarios), no sólo km.
const nearestNeighborByTime = (paradas, startCoords = null) => {
  const withCoords = paradas.filter((p) => p.direccion_lat != null && p.direccion_lon != null);
  const withoutCoords = paradas.filter((p) => p.direccion_lat == null || p.direccion_lon == null);
  if (withCoords.length === 0) return { ordered: paradas, geocoded: 0 };

  const conHorario = withCoords
    .filter((p) => p.horario_entrega_desde || p.horario_entrega_hasta)
    .sort((a, b) => {
      const at = a.horario_entrega_desde || a.horario_entrega_hasta || "23:59";
      const bt = b.horario_entrega_desde || b.horario_entrega_hasta || "23:59";
      return at.localeCompare(bt);
    });
  const sinHorario = withCoords.filter((p) => !(p.horario_entrega_desde || p.horario_entrega_hasta));

  const ordered = [...conHorario];
  let last = ordered.length > 0
    ? { lat: ordered[ordered.length - 1].direccion_lat, lon: ordered[ordered.length - 1].direccion_lon }
    : (startCoords && startCoords.lat != null ? startCoords : null);

  const remaining = [...sinHorario];
  while (remaining.length > 0) {
    let bestIdx = 0; let bestD = Infinity;
    remaining.forEach((p, i) => {
      const d = last
        ? distKm(last, { lat: p.direccion_lat, lon: p.direccion_lon })
        : (p.direccion_lon - 90); // sin punto de partida: primero NO (menor lon)
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    const chosen = remaining.splice(bestIdx, 1)[0];
    ordered.push(chosen);
    last = { lat: chosen.direccion_lat, lon: chosen.direccion_lon };
  }
  return { ordered: [...ordered, ...withoutCoords], geocoded: withCoords.length };
};

// Calcula la información de tramos/llegadas para una lista de paradas de un chofer.
// - `rows`: paradas en su orden de recorrido ({parada, ...}).
// - `startCoords`: coordenadas del depósito (o null si desconocidas).
// - `horaSalidaMin`: minutos desde 00:00 (o null si no se seteó el horario).
// - `realLegs`: array paralelo a `rows` con datos reales de OSRM (opcional).
//     realLegs[i] = { from_prev_sec, from_prev_km, cum_sec } o null si no hay dato para esa parada.
// - `overrides`: mapa `{ [paradaId]: HH:MM }` con horarios de llegada forzados
//   por el usuario. Al encontrar uno, el elapsed se resetea para que la
//   llegada coincida exactamente con lo pedido; las paradas siguientes
//   se recalculan a partir de ese punto (efecto cascada).
//
// Retorna un array paralelo a `rows` con { legKm, legMin, llegadaMin, real, override }.
//   - Si hay realLegs[i] válido → usa esos valores (ruta REAL por auto vía OSRM).
//   - Si no hay coordenadas → usa MINUTOS_TRAMO_SIN_COORDS como fallback (para
//     que el ETA no se rompa cuando una parada no está geocodificada aún).
//   - Fallback general: distancia haversine × 1.3 a 30 km/h.
// Se suma MINUTOS_POR_PARADA en cada parada antes de partir a la siguiente.
const computeLegInfo = (rows, startCoords, horaSalidaMin, realLegs = null, overrides = {}) => {
  const info = [];
  let prev = startCoords && startCoords.lat != null ? startCoords : null;
  let elapsed = 0;
  rows.forEach((r, i) => {
    const p = r.parada;
    let legKm = null, legMin = null, real = false;
    const rl = realLegs && realLegs[i];
    if (rl && rl.from_prev_sec != null) {
      legMin = rl.from_prev_sec / 60;
      legKm = rl.from_prev_km;
      real = true;
    } else {
      const tieneCoords = p.direccion_lat != null && p.direccion_lon != null;
      if (prev && tieneCoords) {
        const km = distKm(prev, { lat: p.direccion_lat, lon: p.direccion_lon }) * 1.3;
        legKm = km;
        legMin = (km / VELOCIDAD_KMH) * 60;
      } else {
        // Sin coordenadas suficientes: usamos un tramo urbano promedio para
        // que el ETA no se rompa cuando una parada no está geocodificada aún.
        // Esto también hace que la cascada de horarios siga funcionando.
        legMin = MINUTOS_TRAMO_SIN_COORDS;
      }
    }
    if (legMin != null) elapsed += legMin;

    // Override manual: la llegada la manda el usuario.
    let llegadaMin = horaSalidaMin != null && legMin != null ? horaSalidaMin + elapsed : null;
    let override = false;
    const forced = overrides ? hhmmToMin(overrides[p.id] || "") : null;
    if (forced != null && horaSalidaMin != null) {
      llegadaMin = forced;
      // Recalculamos elapsed para que las siguientes paradas partan desde acá.
      elapsed = forced - horaSalidaMin;
      override = true;
    }

    info.push({ legKm, legMin, llegadaMin, real, override });
    if (legMin != null) elapsed += MINUTOS_POR_PARADA;
    if (p.direccion_lat != null && p.direccion_lon != null) {
      prev = { lat: p.direccion_lat, lon: p.direccion_lon };
    }
  });
  return info;
};

export default function LogisticaPage() {
  const [fecha, setFecha] = useState(todayISO());
  const [groupBy, setGroupBy] = useState("ninguno");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editAsignacion, setEditAsignacion] = useState({});
  // Nueva parada extra (ad-hoc: buscar cobro, ir a proveedor, etc.)
  const [nuevaParadaOpen, setNuevaParadaOpen] = useState(false);
  const [nuevaParada, setNuevaParada] = useState({
    descripcion: "", monto: "", fecha_parada: "",
    direccion_calle: "", direccion_numero: "",
    direccion_localidad: "", direccion_provincia: "", direccion_cp: "",
    direccion_lat: null, direccion_lon: null, direccion_libre: "", telefono: "",
  });
  const [savingParada, setSavingParada] = useState(false);
  const [deleteParadaExtraId, setDeleteParadaExtraId] = useState(null);
  const [ordenOptimizado, setOrdenOptimizado] = useState({});
  const [ordenPorChofer, setOrdenPorChofer] = useState({}); // { [chofer]: [pedidoId, ...] }
  // Drag & drop de paradas: { key: `${d.key}`, fromIdx: number } | null
  const [dragParada, setDragParada] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Horario de salida a reparto por chofer viaje (HH:MM). Persistido por fecha en localStorage.
  // Init lazy desde localStorage para evitar que el effect de "save" pise el valor con {}
  // en el primer render (race condition al remount al cambiar de pestaña).
  const _todayFecha = todayISO();
  const [horarioSalidaPorChofer, setHorarioSalidaPorChofer] = useState(() => {
    try {
      const raw = localStorage.getItem(`${SALIDA_STORAGE_KEY}.${_todayFecha}`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [vehiculosPorChoferViaje, setVehiculosPorChoferViaje] = useState(() => {
    try {
      const raw = localStorage.getItem(`${VEHICULOS_STORAGE_KEY}.${_todayFecha}`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [splitsPorChofer, setSplitsPorChofer] = useState(() => {
    try {
      const raw = localStorage.getItem(`${SPLITS_STORAGE_KEY}.${_todayFecha}`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Cache de ETA real por chofer viaje (OSRM). Se invalida al cambiar orden/split/roundTrip/origen.
  const [routeEtaCache, setRouteEtaCache] = useState({});
  // Overrides manuales de hora de llegada por parada: { [paradaId]: "HH:MM" }.
  // El usuario puede editar el ETA desde la fila; se recalcula cascada.
  // Init lazy con el cache local para no perder los overrides al remount.
  // La fuente de verdad (backend) los refresca en un efecto aparte.
  const [llegadaOverridePorParada, setLlegadaOverridePorParada] = useState(() => {
    try {
      const raw = localStorage.getItem(`${LLEGADA_OVERRIDES_CACHE_KEY}.${_todayFecha}`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Paradas seleccionadas para reordenar manualmente (por VIAJE, expand/collapse).
  const [expandedViaje, setExpandedViaje] = useState(() => new Set());
  const [loadingEtaChofer, setLoadingEtaChofer] = useState(null);
  const [transportistas, setTransportistas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [origenRuta, setOrigenRuta] = useState(() => {
    try {
      return localStorage.getItem(ORIGEN_STORAGE_KEY) || DEFAULT_ORIGEN;
    } catch {
      return DEFAULT_ORIGEN;
    }
  });
  const [origenCoords, setOrigenCoords] = useState(() => {
    try {
      const raw = localStorage.getItem(ORIGEN_COORDS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.lat != null && parsed?.lon != null && parsed?.for === (localStorage.getItem(ORIGEN_STORAGE_KEY) || DEFAULT_ORIGEN)) {
        return { lat: parsed.lat, lon: parsed.lon };
      }
      return null;
    } catch { return null; }
  });
  const [geocodingOrigen, setGeocodingOrigen] = useState(false);
  const [roundTrip, setRoundTrip] = useState(() => {
    try { return localStorage.getItem(ROUND_TRIP_STORAGE_KEY) === "1"; } catch { return false; }
  });
  const lastReqId = useMemo(() => ({ current: 0 }), []);

  useEffect(() => {
    try { localStorage.setItem(ROUND_TRIP_STORAGE_KEY, roundTrip ? "1" : "0"); } catch { /* silent */ }
  }, [roundTrip]);

  // Ref para trackear la fecha ya cargada. Los effects de "save" NO deben
  // dispararse hasta que el effect de "load" haya poblado el estado desde
  // localStorage, si no pisan los valores buenos con {} en el remount.
  const loadedFechaRef = useRef(_todayFecha);

  // Horarios de salida por chofer: persistidos por fecha para no mezclarse entre días.
  useEffect(() => {
    // Si es la fecha con la que arrancamos (ya cargada en el lazy init), no re-cargamos.
    if (loadedFechaRef.current === fecha) return;
    try {
      const rawS = localStorage.getItem(`${SALIDA_STORAGE_KEY}.${fecha}`);
      const rawV = localStorage.getItem(`${VEHICULOS_STORAGE_KEY}.${fecha}`);
      const rawSp = localStorage.getItem(`${SPLITS_STORAGE_KEY}.${fecha}`);
      const rawCache = localStorage.getItem(`${LLEGADA_OVERRIDES_CACHE_KEY}.${fecha}`);
      setHorarioSalidaPorChofer(rawS ? JSON.parse(rawS) : {});
      setVehiculosPorChoferViaje(rawV ? JSON.parse(rawV) : {});
      setSplitsPorChofer(rawSp ? JSON.parse(rawSp) : {});
      setLlegadaOverridePorParada(rawCache ? JSON.parse(rawCache) : {});
    } catch {
      setHorarioSalidaPorChofer({});
      setVehiculosPorChoferViaje({});
      setSplitsPorChofer({});
      setLlegadaOverridePorParada({});
    }
    // Hidratar splits desde backend (fuente de verdad compartida entre operadores).
    // Se lanza en paralelo — si backend responde, sobreescribe localStorage.
    api.get("/logistica/splits-chofer", { params: { fecha_envio: fecha } })
      .then(({ data }) => {
        if (data && typeof data === "object" && Object.keys(data).length > 0) {
          setSplitsPorChofer(data);
        }
      })
      .catch(() => { /* silent */ });
    loadedFechaRef.current = fecha;
  }, [fecha]);
  useEffect(() => {
    if (loadedFechaRef.current !== fecha) return;
    try {
      localStorage.setItem(`${SALIDA_STORAGE_KEY}.${fecha}`, JSON.stringify(horarioSalidaPorChofer));
    } catch { /* silent */ }
  }, [horarioSalidaPorChofer, fecha]);
  useEffect(() => {
    if (loadedFechaRef.current !== fecha) return;
    try {
      localStorage.setItem(`${VEHICULOS_STORAGE_KEY}.${fecha}`, JSON.stringify(vehiculosPorChoferViaje));
    } catch { /* silent */ }
  }, [vehiculosPorChoferViaje, fecha]);
  useEffect(() => {
    if (loadedFechaRef.current !== fecha) return;
    try {
      localStorage.setItem(`${SPLITS_STORAGE_KEY}.${fecha}`, JSON.stringify(splitsPorChofer));
    } catch { /* silent */ }
    // Sincronizar con backend (fuente de verdad entre Logística y Control de Salida).
    // Enviamos únicamente los choferes presentes en el state (no hace falta borrar
    // los que ya no están porque el endpoint borra al recibir array vacío).
    Object.entries(splitsPorChofer).forEach(([chofer, ids]) => {
      api.post("/logistica/splits-chofer", {
        fecha_envio: fecha,
        chofer,
        split_paradas_ids: Array.isArray(ids) ? ids : [],
      }).catch(() => { /* silent — persistencia best-effort */ });
    });
  }, [splitsPorChofer, fecha]);

  // Overrides de llegada: la fuente de verdad es el backend (compartida entre
  // operadores). El fetch:
  //  - Corre siempre que cambia la fecha (para traer los overrides del día).
  //  - En el primer mount, sólo corre si el cache local está vacío. Si ya hay
  //    overrides en state (desde el lazy init), asumimos que están al día y no
  //    los pisamos con una respuesta que podría ser stale respecto a un PUT
  //    que el usuario dispare al mismo tiempo.
  const fetchedOverridesForFechaRef = useRef(null);
  useEffect(() => {
    const alreadyFetched = fetchedOverridesForFechaRef.current === fecha;
    const cacheIsPopulated = Object.keys(llegadaOverridePorParada || {}).length > 0;
    if (alreadyFetched && cacheIsPopulated) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/logistica/llegada-overrides", { params: { fecha } });
        if (cancelled) return;
        const remote = data?.overrides || {};
        // MERGE: preservamos las claves locales que ya existan (por si el
        // usuario editó justo antes de que llegara la respuesta).
        setLlegadaOverridePorParada((prev) => ({ ...remote, ...prev }));
        try {
          localStorage.setItem(`${LLEGADA_OVERRIDES_CACHE_KEY}.${fecha}`, JSON.stringify(remote));
        } catch { /* silent */ }
        fetchedOverridesForFechaRef.current = fecha;
      } catch {
        // Si falla el fetch, dejamos el cache local que ya está en el state.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);
  // Snapshot local sólo para bootstrap veloz al re-entrar (no es la fuente de verdad).
  useEffect(() => {
    try {
      localStorage.setItem(`${LLEGADA_OVERRIDES_CACHE_KEY}.${fecha}`, JSON.stringify(llegadaOverridePorParada));
    } catch { /* silent */ }
  }, [llegadaOverridePorParada, fecha]);

  // Persistir origen en localStorage
  useEffect(() => {
    try { localStorage.setItem(ORIGEN_STORAGE_KEY, origenRuta); } catch { /* silent */ }
  }, [origenRuta]);

  // Geocodificar el origen (con debounce). Guarda coords en localStorage vinculadas
  // al texto exacto de la dirección para no re-geocodificar si no cambió.
  useEffect(() => {
    const addr = origenRuta.trim();
    if (!addr) { setOrigenCoords(null); return; }
    // Si ya tenemos coords para este texto, no re-geocodificar.
    try {
      const raw = localStorage.getItem(ORIGEN_COORDS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.for === addr && parsed?.lat != null && parsed?.lon != null) {
          setOrigenCoords({ lat: parsed.lat, lon: parsed.lon });
          return;
        }
      }
    } catch { /* ignore */ }

    const t = setTimeout(async () => {
      setGeocodingOrigen(true);
      try {
        const { data } = await api.post("/geocode/parse", { address: addr });
        if (data?.lat != null && data?.lon != null) {
          const lat = parseFloat(data.lat);
          const lon = parseFloat(data.lon);
          if (!isNaN(lat) && !isNaN(lon)) {
            setOrigenCoords({ lat, lon });
            try {
              localStorage.setItem(ORIGEN_COORDS_STORAGE_KEY, JSON.stringify({ lat, lon, for: addr }));
            } catch { /* ignore */ }
          }
        } else {
          setOrigenCoords(null);
        }
      } catch {
        setOrigenCoords(null);
      } finally {
        setGeocodingOrigen(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [origenRuta]);

  // Cargar choferes y vehículos desde el módulo REC (fuente única de verdad)
  useEffect(() => {
    (async () => {
      try {
        const [t, v] = await Promise.all([
          api.get("/rec/transportistas"),
          api.get("/rec/vehiculos"),
        ]);
        setTransportistas(Array.isArray(t.data) ? t.data : []);
        setVehiculos(Array.isArray(v.data) ? v.data : []);
      } catch { /* silencioso; los inputs siguen funcionando como texto libre */ }
    })();
  }, []);

  const load = useCallback(async () => {
    const myId = ++lastReqId.current;
    setLoading(true);
    try {
      const { data } = await api.get("/logistica/hoja-reparto", { params: { fecha, group_by: groupBy } });
      if (myId !== lastReqId.current) return;
      setData(data);
      setExpanded(new Set((data?.grupos || []).map((g) => g.clave)));
      // Sincronizar assignments desde backend
      const nextAsig = {};
      const nextOrden = {};
      (data?.grupos || []).forEach((g) => {
        if (g.chofer || g.vehiculo) nextAsig[g.clave] = { chofer: g.chofer || "", vehiculo: g.vehiculo || "" };
        if (Array.isArray(g.orden_paradas) && g.orden_paradas.length > 0) nextOrden[g.clave] = g.orden_paradas;
      });
      setEditAsignacion(nextAsig);
      setOrdenOptimizado(nextOrden);
      // Cargar orden manual por chofer persistido en backend
      setOrdenPorChofer(data?.orden_por_chofer || {});
    } catch {
      if (myId === lastReqId.current) toast.error("Error al cargar la hoja de reparto");
    } finally {
      if (myId === lastReqId.current) setLoading(false);
    }
  }, [fecha, groupBy, lastReqId]);
  useEffect(() => { load(); }, [load]);

  // ── Parada extra: crear / eliminar ───────────────────────────────────
  const guardarNuevaParada = async () => {
    if (!nuevaParada.descripcion.trim()) {
      toast.error("Ingresá una descripción");
      return;
    }
    setSavingParada(true);
    try {
      const fechaParada = (nuevaParada.fecha_parada || fecha || "").trim();
      if (fechaParada && fecha && fechaParada < fecha) {
        toast.error("La fecha de la parada no puede ser anterior a la fecha visible");
        setSavingParada(false);
        return;
      }
      await api.post("/logistica/paradas-extra", {
        fecha: fechaParada,
        descripcion: nuevaParada.descripcion.trim(),
        monto: nuevaParada.monto ? Number(nuevaParada.monto) : 0,
        direccion_calle: nuevaParada.direccion_calle || null,
        direccion_numero: nuevaParada.direccion_numero || null,
        direccion_localidad: nuevaParada.direccion_localidad || null,
        direccion_provincia: nuevaParada.direccion_provincia || null,
        direccion_cp: nuevaParada.direccion_cp || null,
        direccion_libre: nuevaParada.direccion_libre || null,
        direccion_lat: nuevaParada.direccion_lat,
        direccion_lon: nuevaParada.direccion_lon,
        telefono: nuevaParada.telefono || null,
      });
      const msg = fechaParada && fechaParada !== fecha
        ? `Parada extra agregada para el ${fechaParada}`
        : "Parada extra agregada";
      toast.success(msg);
      setNuevaParadaOpen(false);
      setNuevaParada({
        descripcion: "", monto: "", fecha_parada: "",
        direccion_calle: "", direccion_numero: "",
        direccion_localidad: "", direccion_provincia: "", direccion_cp: "",
        direccion_lat: null, direccion_lon: null, direccion_libre: "", telefono: "",
      });
      // Sólo recargamos si la parada se agregó para el día visible
      if (!fechaParada || fechaParada === fecha) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar la parada");
    } finally {
      setSavingParada(false);
    }
  };

  const confirmarEliminarParadaExtra = async () => {
    if (!deleteParadaExtraId) return;
    try {
      await api.delete(`/logistica/paradas-extra/${deleteParadaExtraId}`);
      toast.success("Parada extra eliminada");
      setDeleteParadaExtraId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const toggle = (k) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const groupLabel = useMemo(() => ({
    localidad: "Localidad / Barrio",
    provincia: "Provincia",
    cp: "Código Postal",
    ninguno: "Sin agrupar",
  }[groupBy]), [groupBy]);

  // Reordena las paradas del grupo según el orden optimizado (si existe)
  const paradasOrdenadas = (grupo) => {
    const orden = ordenOptimizado[grupo.clave];
    if (!orden || orden.length === 0) return grupo.paradas;
    const map = new Map(grupo.paradas.map((p) => [p.id, p]));
    const arr = [];
    orden.forEach((id) => { const p = map.get(id); if (p) { arr.push(p); map.delete(id); } });
    // Agregar cualquier parada nueva al final
    return [...arr, ...map.values()];
  };

  // Calcula distancia (km) y tiempo estimado (min) del recorrido de un grupo.
  // Suma tramos: origen → parada[0] → parada[1] → ... → parada[N]. Si roundTrip, cierra en origen.
  // Sólo cuenta paradas con lat/lon. Retorna { km, minutos, tramos_calculados, tramos_totales }.
  const calcularRecorrido = (grupo) => {
    const paradas = paradasOrdenadas(grupo);
    const conCoords = paradas.filter((p) => p.direccion_lat != null && p.direccion_lon != null);
    const tramosTotales = paradas.length + (origenCoords ? 1 : 0) + (roundTrip && origenCoords ? 1 : 0) - 1;
    if (conCoords.length === 0 || !origenCoords) {
      return { km: null, minutos: null, tramosCalculados: 0, tramosTotales: Math.max(tramosTotales, 0) };
    }
    let km = 0;
    let tramosCalculados = 0;
    // Origen → primera parada con coords
    km += distKm(origenCoords, { lat: conCoords[0].direccion_lat, lon: conCoords[0].direccion_lon });
    tramosCalculados += 1;
    // Entre paradas consecutivas
    for (let i = 1; i < conCoords.length; i += 1) {
      const a = { lat: conCoords[i - 1].direccion_lat, lon: conCoords[i - 1].direccion_lon };
      const b = { lat: conCoords[i].direccion_lat, lon: conCoords[i].direccion_lon };
      km += distKm(a, b);
      tramosCalculados += 1;
    }
    // Round-trip: última parada → origen
    if (roundTrip) {
      const last = conCoords[conCoords.length - 1];
      km += distKm({ lat: last.direccion_lat, lon: last.direccion_lon }, origenCoords);
      tramosCalculados += 1;
    }
    // Ajuste por trayecto real vs línea recta (factor 1.3 típico en zonas urbanas)
    const kmReal = km * 1.3;
    const minutosViaje = (kmReal / VELOCIDAD_KMH) * 60;
    const minutosParadas = paradas.length * MINUTOS_POR_PARADA;
    return {
      km: kmReal,
      minutos: minutosViaje + minutosParadas,
      tramosCalculados,
      tramosTotales: Math.max(tramosTotales, tramosCalculados),
    };
  };

  const fmtDuracion = (min) => {
    if (min == null || !Number.isFinite(min)) return "—";
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
  };

  const guardarAsignacion = async (clave, patch) => {
    const current = editAsignacion[clave] || {};
    const next = { ...current, ...patch };
    setEditAsignacion((s) => ({ ...s, [clave]: next }));
    try {
      await api.post("/logistica/asignaciones", {
        fecha_envio: fecha, group_by: groupBy, clave,
        chofer: next.chofer ?? null, vehiculo: next.vehiculo ?? null,
      });
    } catch {
      toast.error("No se pudo guardar la asignación");
    }
  };

  // Guarda asignación individual de una parada (pedido).
  // Actualiza el `data` local para reflejar el cambio sin recargar todo.
  const guardarParadaAsignacion = async (pedidoId, patch) => {
    const nuevoDato = { ...(patch || {}) };
    // Update UI inmediatamente
    setData((prev) => {
      if (!prev) return prev;
      const nextGrupos = prev.grupos.map((g) => ({
        ...g,
        paradas: g.paradas.map((par) => (par.id === pedidoId ? { ...par, ...nuevoDato } : par)),
      }));
      return { ...prev, grupos: nextGrupos };
    });
    try {
      // Determinar chofer/vehículo actuales (leer del state luego del update)
      const paradaActual = (data?.grupos || [])
        .flatMap((g) => g.paradas)
        .find((par) => par.id === pedidoId) || {};
      const merged = { ...paradaActual, ...nuevoDato };
      await api.post("/logistica/paradas-asignaciones", {
        fecha_envio: fecha,
        pedido_id: pedidoId,
        chofer: merged.chofer_individual ?? null,
        vehiculo: merged.vehiculo_individual ?? null,
      });
    } catch {
      toast.error("No se pudo guardar la asignación de la parada");
    }
  };

  const enviarWhatsapp = (grupo) => {
    const asig = editAsignacion[grupo.clave] || {};
    const paradas = paradasOrdenadas(grupo);
    const recorrido = calcularRecorrido(grupo);
    const header = [
      `🛵 *Hoja de Reparto — ${grupo.clave}*`,
      `📅 ${fecha}`,
      origenRuta && `🚩 Salida: ${origenRuta}`,
      asig.chofer && `👤 Chofer: ${asig.chofer}`,
      asig.vehiculo && `🚚 Vehículo: ${asig.vehiculo}`,
      `📦 ${grupo.total_paradas} paradas · ${grupo.total_kg} kg`,
      recorrido.km != null && `🛣️ ${recorrido.km.toFixed(1)} km · ⏱ ${fmtDuracion(recorrido.minutos)}${roundTrip ? " (vuelta al depósito incluida)" : ""}`,
      "━━━━━━━━━━━━━━━━━━",
    ].filter(Boolean).join("\n");

    const paradasTexto = paradas.map((p, i) => {
      const direccion = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "";
      const zona = [p.direccion_localidad, p.direccion_provincia, p.direccion_cp && `CP ${p.direccion_cp}`].filter(Boolean).join(", ");
      // Link a Google Maps (funciona con coords o con dirección)
      const query = (p.direccion_lat && p.direccion_lon)
        ? `${p.direccion_lat},${p.direccion_lon}`
        : `${direccion}, ${zona}`.trim();
      const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
      const partes = [
        `*${i + 1}. ${p.cliente_nombre || "—"}*`,
        `📍 ${direccion}${zona ? ", " + zona : ""}`,
        p.cliente_telefono && `📞 ${p.cliente_telefono}`,
        `🥩 ${p.items_resumen?.preview || "—"} (${p.items_resumen?.total_kg || 0} kg)`,
        isPagado(p)
          ? `✅ *YA PAGO* (${p.pago_metodo || "no cobra"})`
          : (esEfectivo(p)
              ? `💰 A COBRAR: ${fmtMoney(p.total)}`
              : `💳 *${p.pago_metodo}* (no cobra)`),
        `🗺️ ${mapLink}`,
      ].filter(Boolean).join("\n");
      return partes;
    }).join("\n\n");

    const mensaje = `${header}\n\n${paradasTexto}\n\n🗺️ Ruta completa: ${rutaCompletaUrl(grupo)}`;
    const url = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
    window.open(url, "_blank");
  };

  // Construye la URL de Google Maps Directions con origin + waypoints + destination
  // usando el orden actual de paradas (respeta la ruta optimizada si existe).
  // Si `roundTrip` está activo, el destino final es el origen (cierra el bucle).
  const rutaCompletaUrl = (grupo) => {
    const paradas = paradasOrdenadas(grupo);
    if (paradas.length === 0) return "";
    const dirDeParada = (p) => {
      if (p.direccion_lat && p.direccion_lon) return `${p.direccion_lat},${p.direccion_lon}`;
      const dir = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "";
      const zona = [p.direccion_localidad, p.direccion_provincia].filter(Boolean).join(", ");
      return `${dir}${zona ? ", " + zona : ""}`.trim();
    };
    const origen = origenRuta || DEFAULT_ORIGEN;
    const origin = encodeURIComponent(origen);
    let destination;
    let waypointList;
    if (roundTrip) {
      // El origen es también el destino final; todas las paradas son waypoints.
      destination = encodeURIComponent(origen);
      waypointList = paradas.map(dirDeParada);
    } else {
      // Última parada = destino; el resto son waypoints.
      destination = encodeURIComponent(dirDeParada(paradas[paradas.length - 1]));
      waypointList = paradas.slice(0, -1).map(dirDeParada);
    }
    const waypoints = waypointList.map(encodeURIComponent).join("|");
    const wpParam = waypoints ? `&waypoints=${waypoints}` : "";
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${wpParam}&travelmode=driving`;
  };

  const abrirRutaCompleta = (grupo) => {
    const url = rutaCompletaUrl(grupo);
    if (!url) return toast.warning("No hay paradas para armar la ruta.");
    window.open(url, "_blank");
  };

  // Abre Google Maps Directions desde el depósito hacia UNA parada específica.
  // Se usa desde cada fila de la hoja de reparto para tener la ruta puntual.
  const abrirRutaParada = (parada) => {
    if (!parada) return;
    const dir = [parada.direccion_calle, parada.direccion_numero].filter(Boolean).join(" ") || parada.direccion_libre || "";
    const zona = [parada.direccion_localidad, parada.direccion_provincia].filter(Boolean).join(", ");
    const destination = (parada.direccion_lat && parada.direccion_lon)
      ? `${parada.direccion_lat},${parada.direccion_lon}`
      : `${dir}${zona ? ", " + zona : ""}`.trim();
    if (!destination) return toast.warning("Esta parada no tiene dirección utilizable.");
    const origen = origenRuta || DEFAULT_ORIGEN;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origen)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
    window.open(url, "_blank");
  };

  // Diálogo para elegir a qué chofer mandar el WhatsApp cuando la parada
  // no tiene chofer asignado (o el asignado no tiene teléfono cargado).
  const [waPicker, setWaPicker] = useState(null); // { parada, mensaje } | null
  const [waConfigOpen, setWaConfigOpen] = useState(false);
  const [waConfig, setWaConfig] = useState({
    campos: {
      nombre_cliente: true, num_pedido: false, direccion: true, telefono_cliente: true,
      estado_pago: true, monto_a_cobrar: true, link_maps: true,
      especificacion: false, hora_estimada: false, observaciones: false,
    },
    encabezado: "",
    pie: "",
  });

  useEffect(() => {
    api.get("/logistica/config/wa-mensaje")
      .then(({ data }) => data && setWaConfig(data))
      .catch(() => {});
  }, []);

  // Genera el texto de WhatsApp para UNA parada respetando la config del usuario.
  const armarMensajeParada = (parada) => {
    const c = waConfig.campos || {};
    const dir = [parada.direccion_calle, parada.direccion_numero].filter(Boolean).join(" ") || parada.direccion_libre || "";
    const zona = [parada.direccion_localidad, parada.direccion_provincia, parada.direccion_cp && `CP ${parada.direccion_cp}`].filter(Boolean).join(", ");
    const query = (parada.direccion_lat && parada.direccion_lon)
      ? `${parada.direccion_lat},${parada.direccion_lon}`
      : `${dir}${zona ? ", " + zona : ""}`;
    const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    // hora estimada de llegada (si está persistida por el usuario o autocalculada)
    let hh = "";
    try {
      hh = (parada?.hora_estimada || parada?.eta || parada?.hora_llegada || "").toString();
    } catch { hh = ""; }
    const especificacion = (parada.items || [])
      .map((it) => it.especificacion).filter(Boolean).join(", ");
    const pagoLinea = isPagado(parada)
      ? `✅ *YA PAGO* (${parada.pago_metodo || "no cobra"})`
      : (esEfectivo(parada)
          ? (c.monto_a_cobrar ? `💰 A COBRAR: ${fmtMoney(parada.total)}` : "💰 A COBRAR")
          : `💳 *${parada.pago_metodo}* (no cobra)`);
    const lineas = [
      waConfig.encabezado ? waConfig.encabezado.trim() : "",
      c.nombre_cliente && `*${parada.cliente_nombre || "—"}*`,
      c.num_pedido && parada.numero ? `📦 Pedido #${parada.numero}` : "",
      c.direccion && `📍 ${dir}${zona ? ", " + zona : ""}`,
      c.telefono_cliente && parada.cliente_telefono && `📞 ${parada.cliente_telefono}`,
      c.especificacion && especificacion && `📝 ${especificacion}`,
      c.hora_estimada && hh && `⏰ ETA ${hh}`,
      c.observaciones && parada.observaciones ? `💬 ${parada.observaciones}` : "",
      c.estado_pago && pagoLinea,
      c.link_maps && `🗺️ ${mapLink}`,
      waConfig.pie ? waConfig.pie.trim() : "",
    ].filter(Boolean);
    return lineas.join("\n");
  };

  // Normaliza un número AR para wa.me (agrega prefijo 549 si es un móvil AR sin código país)
  const normalizarTelefonoWa = (raw) => {
    let t = String(raw || "").replace(/[^\d+]/g, "");
    if (!t) return "";
    if (t.startsWith("+")) t = t.slice(1);
    // Casos comunes AR: 11XXXXXXXX (10 dig) → 549 + 11XXXXXXXX
    if (/^\d{10}$/.test(t)) t = "549" + t;
    else if (/^0?\d{2,4}15\d{6,8}$/.test(t)) t = "549" + t.replace(/^0?/, "").replace("15", "");
    else if (/^54\d{10,}$/.test(t) && !t.startsWith("549")) t = "549" + t.slice(2);
    return t;
  };

  // Comparte por WhatsApp la información de UNA parada.
  // Prioridad: chofer asignado a la parada → chofer del grupo → selector.
  const enviarWhatsappParada = (parada, choferAsignado = null) => {
    if (!parada) return;
    const mensaje = armarMensajeParada(parada);
    // Encontrar el nombre del chofer efectivo
    const nombreChofer = (choferAsignado || parada.chofer_individual || "").trim();
    if (nombreChofer) {
      const chofer = (transportistas || []).find(
        (t) => String(t.nombre || "").trim().toLowerCase() === nombreChofer.toLowerCase()
      );
      const tel = normalizarTelefonoWa(chofer?.telefono);
      if (tel) {
        const url = `https://wa.me/${tel}?text=${encodeURIComponent(mensaje)}`;
        window.open(url, "_blank");
        return;
      }
      // Chofer asignado pero sin teléfono cargado → avisar y abrir selector
      toast.info(`El chofer "${nombreChofer}" no tiene teléfono cargado. Elegí destinatario.`);
    }
    setWaPicker({ parada, mensaje });
  };

  // URL de Google Maps Directions armada con las paradas de UN chofer (potencialmente
  // cruzando zonas). Respeta el orden actual (optimizado o del grupo) y el toggle roundTrip.
  const rutaChoferUrl = (rows) => {
    const paradas = (rows || []).map((r) => r.parada);
    if (paradas.length === 0) return "";
    const dirDeParada = (p) => {
      if (p.direccion_lat && p.direccion_lon) return `${p.direccion_lat},${p.direccion_lon}`;
      const dir = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "";
      const zona = [p.direccion_localidad, p.direccion_provincia].filter(Boolean).join(", ");
      return `${dir}${zona ? ", " + zona : ""}`.trim();
    };
    const origen = origenRuta || DEFAULT_ORIGEN;
    const origin = encodeURIComponent(origen);
    let destination;
    let waypointList;
    if (roundTrip) {
      destination = encodeURIComponent(origen);
      waypointList = paradas.map(dirDeParada);
    } else {
      destination = encodeURIComponent(dirDeParada(paradas[paradas.length - 1]));
      waypointList = paradas.slice(0, -1).map(dirDeParada);
    }
    const waypoints = waypointList.map(encodeURIComponent).join("|");
    const wpParam = waypoints ? `&waypoints=${waypoints}` : "";
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${wpParam}&travelmode=driving`;
  };

  const abrirRutaChoferMaps = (viaje) => {
    if (!viaje || !viaje.rows || viaje.rows.length === 0) return toast.warning(`${viaje?.label || "El chofer"} no tiene paradas asignadas.`);
    const url = rutaChoferUrl(viaje.rows);
    if (!url) return toast.warning("No se pudo armar la ruta.");
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(
          () => toast.success(`Ruta de ${viaje.label} lista · link copiado`),
          () => {},
        );
      }
    } catch { /* silent */ }
    window.open(url, "_blank");
  };

  // Exporta la hoja de reparto a PDF (A4 vertical) con:
  //  - Encabezado (fecha, origen, totales)
  //  - Un bloque por grupo con chofer/vehículo, km/tiempo y tabla de paradas
  //  - Columna "✓" al inicio para tildar entregas realizadas (checklist del chofer)
  const exportarPDF = () => {
    const grupos = data?.grupos || [];
    if (grupos.length === 0) {
      return toast.warning("No hay paradas para exportar en esta fecha.");
    }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 12;

    // ---------- Encabezado principal ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(146, 26, 14); // #921A0E
    doc.text("HOJA DE REPARTO", pageW / 2, y, { align: "center" });
    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Fecha: ${fecha}   ·   Agrupado por: ${groupLabel}`, pageW / 2, y, { align: "center" });
    y += 5;
    doc.setFontSize(9);
    doc.text(`Salida: ${origenRuta || DEFAULT_ORIGEN}${roundTrip ? "   (ida y vuelta)" : ""}`, pageW / 2, y, { align: "center" });
    y += 5;
    doc.setTextColor(120, 120, 120);
    doc.text(
      `${totalizadores.paradas} paradas · ${totalizadores.grupos} ${groupLabel.toLowerCase()} · ${totalizadores.kg} kg`,
      pageW / 2, y, { align: "center" }
    );
    y += 7;

    // ---------- Grupos ----------
    grupos.forEach((g, idxG) => {
      const paradas = paradasOrdenadas(g);
      const asig = editAsignacion[g.clave] || {};
      const recorrido = calcularRecorrido(g);

      // Salto de página si no entra el header + al menos 2 filas
      if (y > 250) { doc.addPage(); y = 12; }

      // Header del grupo (banda gris)
      doc.setFillColor(240, 240, 240);
      doc.rect(10, y, pageW - 20, 12, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text(`${idxG + 1}. ${g.clave}`, 12, y + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(90, 90, 90);
      const kmTxt = recorrido.km != null
        ? `${recorrido.km.toFixed(1)} km  ·  ${fmtDuracion(recorrido.minutos)}`
        : "km/tiempo N/D";
      doc.text(
        `${g.total_paradas} paradas · ${g.total_kg} kg · ${kmTxt}`,
        12, y + 9.5
      );
      // Asignación (chofer/vehículo) a la derecha
      const asigText = [
        asig.chofer && `Chofer: ${asig.chofer}`,
        asig.vehiculo && `Vehículo: ${asig.vehiculo}`,
      ].filter(Boolean).join("   ·   ") || "Chofer: __________________   Vehículo: __________________";
      doc.setTextColor(50, 50, 50);
      doc.text(asigText, pageW - 12, y + 9.5, { align: "right" });
      y += 14;

      // Tabla de paradas — sin checkbox
      const body = paradas.map((p, i) => {
        const dir = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "—";
        const zona = [p.direccion_localidad, p.direccion_provincia, p.direccion_cp && `CP ${p.direccion_cp}`].filter(Boolean).join(", ");
        const fmtRef = (label, val) => {
          const s = String(val || "").trim();
          if (!s) return null;
          if (/^(piso|p°|p\.|dpto|dto|depto|departamento|timbre|t°|t\.|ref)/i.test(s)) return s;
          return `${label} ${s}`;
        };
        const refPartes = [
          fmtRef("Piso", p.direccion_piso),
          fmtRef("Dpto", p.direccion_dpto),
          fmtRef("Timbre", p.direccion_timbre),
        ].filter(Boolean);
        const refExtra = refPartes.join(" · ");
        const contacto = [p.cliente_nombre || "—", p.cliente_telefono].filter(Boolean).join("\n");
        // Asignación efectiva: individual pisa a la del grupo.
        const choferEfectivo = p.chofer_individual || asig.chofer || "";
        const vehiculoEfectivo = p.vehiculo_individual || asig.vehiculo || "";
        const asigLinea = [choferEfectivo, vehiculoEfectivo].filter(Boolean).join(" · ");
        // Estado de pago: si está pago no mostrar total (el chofer no cobra).
        const pago = esEfectivo(p) && !isPagado(p) ? fmtMoney(p.total) : "";
        return [
          String(i + 1),
          contacto,
          `${dir}${refExtra ? "\n" + refExtra : ""}${zona ? "\n" + zona : ""}`,
          asigLinea || "—",
          pago,
        ];
      });
      autoTable(doc, {
        startY: y,
        margin: { left: 10, right: 10 },
        theme: "grid",
        head: [["#", "Cliente", "Dirección", "Chofer/Veh.", "Pago"]],
        body,
        styles: { fontSize: 8, cellPadding: 1.6, valign: "top", textColor: [40, 40, 40] },
        headStyles: { fillColor: [146, 26, 14], textColor: [255, 255, 255], fontSize: 8, halign: "center" },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 60 },
          2: { cellWidth: 100 },
          3: { cellWidth: 45 },
          4: { cellWidth: "auto", halign: "right", fontStyle: "bold" },
        },
        // Sombrear en verde la columna Pago sólo cuando hay monto a cobrar en efectivo
        didParseCell: (hookData) => {
          if (hookData.section === "body" && hookData.column.index === 4) {
            const parada = paradas[hookData.row.index];
            if (parada && esEfectivo(parada) && !isPagado(parada)) {
              hookData.cell.styles.fillColor = [220, 250, 220];   // verde suave
              hookData.cell.styles.textColor = [22, 101, 52];     // verde oscuro
              hookData.cell.styles.fontStyle = "bold";
            }
          }
        },
        didDrawPage: () => { y = 12; },
      });
      y = (doc.lastAutoTable?.finalY || y) + 6;
    });

    // ---------- Pie totales ----------
    if (y > 270) { doc.addPage(); y = 12; }
    doc.setDrawColor(200, 200, 200);
    doc.line(10, y, pageW - 10, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(146, 26, 14);
    doc.text("Totales del día:", 12, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    doc.text(
      `${totalizadores.paradas} paradas · ${totalizadores.kg} kg`,
      pageW - 12, y, { align: "right" }
    );

    // Footer con página en cada hoja
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Frigorífico La Bife · Hoja de Reparto ${fecha}`, 10, 292);
      doc.text(`Página ${i} de ${total}`, pageW - 10, 292, { align: "right" });
    }

    doc.save(`hoja-reparto-${fecha}.pdf`);
    toast.success("PDF descargado");
  };

  // Reagrupa por chofer efectivo (individual > grupo). Aplica orden custom por chofer si existe.
  // Retorna Map<chofer, rows[]>. Cada row = { grupoOrig, grupoAsig, parada, orden, vehiculo }.
  const buildRowsPorChofer = () => {
    const grupos = data?.grupos || [];
    const porChofer = new Map();
    grupos.forEach((g) => {
      const asig = editAsignacion[g.clave] || {};
      const paradas = paradasOrdenadas(g);
      paradas.forEach((p, idx) => {
        const chofer = (p.chofer_individual || asig.chofer || "").trim() || "Sin asignar";
        const vehiculo = p.vehiculo_individual || asig.vehiculo || "";
        if (!porChofer.has(chofer)) porChofer.set(chofer, []);
        porChofer.get(chofer).push({
          grupoOrig: g.clave, grupoAsig: asig, parada: p, orden: idx + 1, vehiculo,
        });
      });
    });
    // Aplicar orden optimizado por chofer si existe
    Object.entries(ordenPorChofer).forEach(([chofer, ids]) => {
      const rows = porChofer.get(chofer);
      if (!rows || !ids || ids.length === 0) return;
      const map = new Map(rows.map((r) => [r.parada.id, r]));
      const ordered = [];
      ids.forEach((id) => { const r = map.get(id); if (r) { ordered.push(r); map.delete(id); } });
      porChofer.set(chofer, [...ordered, ...map.values()]);
    });
    return porChofer;
  };

  // Lista de VIAJES (chofer × viajeIdx) para el panel superior. Cada chofer puede tener
  // múltiples viajes si el usuario marcó puntos de corte en su ruta. Cada viaje trae:
  //   - key: identifier único (chofer o "chofer|viajeIdx" si hay splits)
  //   - chofer: nombre base
  //   - viajeIdx / numViajes: 0-based / total viajes de ese chofer
  //   - label: "Federico" o "Federico · Viaje 2"
  //   - rows: paradas SOLO de ese viaje (ya recortadas según splits)
  //   - vehiculo: overrideado desde localStorage por (chofer × viaje)
  const viajesList = useMemo(() => {
    const porChofer = buildRowsPorChofer();
    const list = [];
    porChofer.forEach((rows, chofer) => {
      const splits = splitsPorChofer[chofer] || [];
      // Aplicar splits: cortar la lista después de cada parada marcada como punto de corte.
      const segments = [];
      let current = [];
      rows.forEach((r) => {
        current.push(r);
        if (splits.includes(r.parada.id)) {
          segments.push(current);
          current = [];
        }
      });
      if (current.length > 0) segments.push(current);
      const numViajes = segments.length;
      segments.forEach((segRows, viajeIdx) => {
        const key = numViajes > 1 ? `${chofer}|${viajeIdx}` : chofer;
        const vehiculo = vehiculosPorChoferViaje[key] || "";
        const totalKg = segRows.reduce((a, r) => a + (r.parada.items_resumen?.total_kg || 0), 0);
        const totalACobrar = segRows.reduce((a, r) => a + (esEfectivo(r.parada) ? (r.parada.total || 0) : 0), 0);
        list.push({
          key,
          chofer,
          viajeIdx,
          numViajes,
          label: numViajes > 1 ? `${chofer} · Viaje ${viajeIdx + 1}` : chofer,
          rows: segRows,
          vehiculo,
          totalKg: Math.round(totalKg * 1000) / 1000,
          totalACobrar,
          totalParadas: segRows.length,
          optimizado: !!ordenPorChofer[chofer],
        });
      });
    });
    // "Sin asignar" al final; resto por chofer y por viajeIdx
    return list.sort((a, b) => {
      if (a.chofer === "Sin asignar") return 1;
      if (b.chofer === "Sin asignar") return -1;
      if (a.chofer !== b.chofer) return b.totalParadas - a.totalParadas;
      return a.viajeIdx - b.viajeIdx;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editAsignacion, ordenOptimizado, ordenPorChofer, splitsPorChofer, vehiculosPorChoferViaje]);

  // Total de choferes distintos (para el header del panel).
  const totalChoferes = useMemo(
    () => new Set(viajesList.filter((v) => v.chofer !== "Sin asignar").map((v) => v.chofer)).size,
    [viajesList]
  );

  // Mapa `paradaId → HH:MM` con la hora estimada de llegada al pedido según:
  //  - El horario de salida del VIAJE al que pertenece esa parada.
  //  - El orden de las paradas dentro de ese viaje.
  //  - Los tramos reales OSRM (si están cacheados) o estimación Haversine.
  // Se muestra al lado del "#N" de cada parada en la lista de pedidos.
  const llegadasPorParada = useMemo(() => {
    const map = {};
    viajesList.forEach((viaje) => {
      const horaMin = hhmmToMin(horarioSalidaPorChofer[viaje.key] || "");
      if (horaMin == null) return; // sin salida no hay ETA
      const legs = routeEtaCache[viaje.key]?.legs || null;
      const info = computeLegInfo(viaje.rows, origenCoords, horaMin, legs, llegadaOverridePorParada);
      viaje.rows.forEach((r, i) => {
        if (info[i]?.llegadaMin != null) {
          map[r.parada.id] = minToHHMM(info[i].llegadaMin);
        }
      });
    });
    return map;
  }, [viajesList, horarioSalidaPorChofer, routeEtaCache, origenCoords, llegadaOverridePorParada]);

  // Optimiza la ruta de un chofer (todas sus paradas, cruzando splits) por
  // distancia (nearest-neighbor haversine) o por tiempo (nearest-neighbor + horarios).
  const optimizarPorChofer = (chofer, modo = "distancia") => {
    const porChofer = buildRowsPorChofer();
    const rows = porChofer.get(chofer) || [];
    const paradas = rows.map((r) => r.parada);
    if (paradas.length === 0) {
      toast.warning("Este chofer no tiene paradas asignadas.");
      return;
    }
    const algo = modo === "tiempo" ? nearestNeighborByTime : nearestNeighborOrder;
    const { ordered, geocoded } = algo(paradas, origenCoords);
    if (geocoded === 0) {
      toast.warning("Ninguna parada de este chofer tiene coordenadas cargadas.");
      return;
    }
    const orden = ordered.map((p) => p.id);
    setOrdenPorChofer((prev) => ({ ...prev, [chofer]: orden }));
    // Invalidar ETA cache de TODOS los viajes de este chofer (el orden cambió)
    setRouteEtaCache((prev) => {
      const n = { ...prev };
      Object.keys(n).forEach((k) => { if (k === chofer || k.startsWith(`${chofer}|`)) delete n[k]; });
      return n;
    });
    const msgOrigen = origenCoords ? ` desde ${origenRuta.split(",")[0]}` : "";
    const modoLabel = modo === "tiempo" ? "tiempo (horarios + km)" : "distancia (km)";
    toast.success(`Ruta de ${chofer} optimizada por ${modoLabel}${msgOrigen} · ${geocoded}/${paradas.length} con coords`);
  };

  // Mueve una parada arriba/abajo DENTRO DE UN VIAJE. Actualiza el orden global del chofer
  // (que atraviesa todos los viajes) para que el cambio se refleje en el PDF y en Maps.
  const moverParadaViaje = (viaje, indexEnViaje, delta) => {
    reordenarParadaViaje(viaje, indexEnViaje, indexEnViaje + delta);
  };

  // Reordena una parada dentro del mismo viaje moviéndola de `fromIdx` a `toIdx`.
  const reordenarParadaViaje = (viaje, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const porChofer = buildRowsPorChofer();
    const fullRows = porChofer.get(viaje.chofer);
    if (!fullRows) return;
    if (fromIdx < 0 || fromIdx >= viaje.rows.length) return;
    if (toIdx < 0 || toIdx >= viaje.rows.length) return;
    const paradaId = viaje.rows[fromIdx].parada.id;
    const targetParadaId = viaje.rows[toIdx].parada.id;
    const globalIdx = fullRows.findIndex((r) => r.parada.id === paradaId);
    const targetGlobalIdx = fullRows.findIndex((r) => r.parada.id === targetParadaId);
    if (globalIdx < 0 || targetGlobalIdx < 0) return;
    const ids = fullRows.map((r) => r.parada.id);
    const [movida] = ids.splice(globalIdx, 1);
    ids.splice(targetGlobalIdx, 0, movida);
    setOrdenPorChofer((prev) => ({ ...prev, [viaje.chofer]: ids }));
    setRouteEtaCache((prev) => {
      const n = { ...prev };
      Object.keys(n).forEach((k) => { if (k === viaje.chofer || k.startsWith(`${viaje.chofer}|`)) delete n[k]; });
      return n;
    });
    // Persistir en backend (silencioso; los errores no bloquean el UI)
    api.post("/logistica/orden-chofer", {
      fecha_envio: fecha,
      chofer: viaje.chofer,
      orden: ids,
    }).catch(() => toast.error("No se pudo guardar el nuevo orden"));
  };

  // Deshace el corte que separa el viaje `viaje` del anterior — los une en un único viaje.
  // Si al deshacer no quedan más cortes, migra los valores de |0 al key base (chofer).
  const deshacerCorteViaje = (viaje) => {
    if (viaje.viajeIdx === 0) return;
    const porChofer = buildRowsPorChofer();
    const fullRows = porChofer.get(viaje.chofer);
    if (!fullRows) return;
    const firstId = viaje.rows[0]?.parada?.id;
    if (!firstId) return;
    const fullIdx = fullRows.findIndex((r) => r.parada.id === firstId);
    if (fullIdx <= 0) return;
    const paradaAntes = fullRows[fullIdx - 1].parada.id;
    setSplitsPorChofer((prev) => {
      const actual = prev[viaje.chofer] || [];
      if (!actual.includes(paradaAntes)) return prev;
      const filtrado = actual.filter((id) => id !== paradaAntes);
      // Si desaparece el último corte, restaurar key=chofer desde |0
      if (actual.length === 1) {
        setVehiculosPorChoferViaje((v) => {
          const n = { ...v };
          if (n[`${viaje.chofer}|0`] !== undefined) n[viaje.chofer] = n[`${viaje.chofer}|0`];
          Object.keys(n).forEach((k) => { if (k.startsWith(`${viaje.chofer}|`)) delete n[k]; });
          return n;
        });
        setHorarioSalidaPorChofer((h) => {
          const n = { ...h };
          if (n[`${viaje.chofer}|0`] !== undefined) n[viaje.chofer] = n[`${viaje.chofer}|0`];
          Object.keys(n).forEach((k) => { if (k.startsWith(`${viaje.chofer}|`)) delete n[k]; });
          return n;
        });
      }
      return { ...prev, [viaje.chofer]: filtrado };
    });
    setRouteEtaCache((prev) => {
      const n = { ...prev };
      Object.keys(n).forEach((k) => { if (k === viaje.chofer || k.startsWith(`${viaje.chofer}|`)) delete n[k]; });
      return n;
    });
    toast.success(`Corte deshecho — viajes ${viaje.viajeIdx} y ${viaje.viajeIdx + 1} unidos en uno solo`);
  };

  // Alterna un corte después de una parada. N cortes ⇒ N+1 viajes.
  // Cuando se agrega el primer corte, migra vehículo y horario base (key=chofer) hacia
  // el nuevo viaje 0 (key=`${chofer}|0`) para no perder los valores previos.
  const toggleCorteEnParada = (chofer, paradaId) => {
    setSplitsPorChofer((prev) => {
      const actual = prev[chofer] || [];
      const yaEsta = actual.includes(paradaId);
      const nuevo = yaEsta ? actual.filter((id) => id !== paradaId) : [...actual, paradaId];
      // Migración: cuando pasamos de 0 → 1+ cortes, mover key=chofer a key=chofer|0.
      if (!yaEsta && actual.length === 0) {
        setVehiculosPorChoferViaje((v) => {
          if (v[chofer] === undefined) return v;
          const n = { ...v, [`${chofer}|0`]: v[chofer] };
          delete n[chofer];
          return n;
        });
        setHorarioSalidaPorChofer((h) => {
          if (h[chofer] === undefined) return h;
          const n = { ...h, [`${chofer}|0`]: h[chofer] };
          delete n[chofer];
          return n;
        });
      }
      // Migración inversa: cuando volvemos de 1 → 0 cortes, restaurar key=chofer desde |0.
      if (yaEsta && actual.length === 1) {
        setVehiculosPorChoferViaje((v) => {
          const n = { ...v };
          if (n[`${chofer}|0`] !== undefined) { n[chofer] = n[`${chofer}|0`]; }
          Object.keys(n).forEach((k) => { if (k.startsWith(`${chofer}|`)) delete n[k]; });
          return n;
        });
        setHorarioSalidaPorChofer((h) => {
          const n = { ...h };
          if (n[`${chofer}|0`] !== undefined) { n[chofer] = n[`${chofer}|0`]; }
          Object.keys(n).forEach((k) => { if (k.startsWith(`${chofer}|`)) delete n[k]; });
          return n;
        });
      }
      return { ...prev, [chofer]: nuevo };
    });
    // Invalidar cache OSRM
    setRouteEtaCache((prev) => {
      const n = { ...prev };
      Object.keys(n).forEach((k) => { if (k === chofer || k.startsWith(`${chofer}|`)) delete n[k]; });
      return n;
    });
  };

  // Consulta al backend los tiempos/distancias REALES de manejo entre paradas (OSRM público).
  // Cacheada por viajeKey. Si falla o no hay origen, devuelve null.
  const fetchRouteEta = async (viajeKey, rows) => {
    if (!origenCoords || !rows || rows.length === 0) return null;
    const hash = `${rows.map((r) => r.parada.id).join(",")}|${roundTrip ? 1 : 0}|${origenCoords.lat.toFixed(4)},${origenCoords.lon.toFixed(4)}`;
    const cached = routeEtaCache[viajeKey];
    if (cached && cached.hash === hash) return cached.legs;
    setLoadingEtaChofer(viajeKey);
    try {
      const { data } = await api.post("/logistica/route-eta", {
        origen_lat: origenCoords.lat,
        origen_lon: origenCoords.lon,
        paradas_ids: rows.map((r) => r.parada.id),
        round_trip: roundTrip,
      });
      if (data?.ok && Array.isArray(data.legs)) {
        setRouteEtaCache((prev) => ({ ...prev, [viajeKey]: { hash, legs: data.legs } }));
        if (data.geocoded_missing > 0) {
          toast.success(`${data.geocoded_missing} dirección(es) recién geocodificadas`);
        }
        if (Array.isArray(data.still_missing) && data.still_missing.length > 0) {
          toast.warning(`${data.still_missing.length} parada(s) sin dirección ubicable → tiempo estimado por línea recta`);
        }
        return data.legs;
      }
      return null;
    } catch (e) {
      // Silencioso: se cae a Haversine en el PDF.
      return null;
    } finally {
      setLoadingEtaChofer(null);
    }
  };

  // Renderiza el PDF individual de un chofer con sus paradas ordenadas.
  // `horaSalidaMin` (opcional) permite calcular la columna "Llegada" (hh:mm) por parada.
  // `realLegs` (opcional) trae tiempos/distancias reales OSRM; si es null se estima por Haversine.
  const renderChoferPDF = (chofer, rows, horaSalidaMin = null, realLegs = null, vehiculoOverride = null) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 12;

    // Header principal
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(146, 26, 14);
    doc.text("HOJA DE REPARTO", pageW / 2, y, { align: "center" });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(`Chofer: ${chofer}`, pageW / 2, y, { align: "center" });
    y += 5;
    // El vehículo se toma del override del panel (chofer × viaje). Si no hay,
    // fallback a los vehículos únicos que tuvieran las paradas (retrocompat).
    const vehiculoLabel = vehiculoOverride
      || [...new Set(rows.map((r) => r.vehiculo).filter(Boolean))].join(" · ");
    if (vehiculoLabel) {
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(`Vehículo: ${vehiculoLabel}`, pageW / 2, y, { align: "center" });
      y += 4;
    }
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    const salidaTxt = horaSalidaMin != null ? `   ·   Salida: ${minToHHMM(horaSalidaMin)} hs` : "";
    doc.text(`Fecha: ${fecha}${salidaTxt}`, pageW / 2, y, { align: "center" });
    y += 4;
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Salida desde: ${origenRuta || DEFAULT_ORIGEN}${roundTrip ? "   (ida y vuelta)" : ""}`, pageW / 2, y, { align: "center" });
    y += 6;

    // Totales del chofer
    const totalKg = rows.reduce((acc, r) => acc + (r.parada.items_resumen?.total_kg || 0), 0);
    doc.setFillColor(250, 245, 244);
    doc.rect(10, y, pageW - 20, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(146, 26, 14);
    doc.text(`${rows.length} parada${rows.length === 1 ? "" : "s"} · ${totalKg} kg`, 12, y + 6);
    const zonas = [...new Set(rows.map((r) => r.grupoOrig))];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Zona${zonas.length > 1 ? "s" : ""}: ${zonas.join(" · ")}`, pageW - 12, y + 6, { align: "right" });
    y += 13;

    // Cálculo de tramos (tiempo entre parada anterior y esta) + llegada estimada.
    // Si `realLegs` viene con datos OSRM se usan; caso contrario, estimación Haversine.
    const legInfo = computeLegInfo(rows, origenCoords, horaSalidaMin, realLegs, llegadaOverridePorParada);

    // Tabla de paradas del chofer (Llegada + Tramo entre Dirección y Pago)
    const body = rows.map((r, i) => {
      const p = r.parada;
      const dir = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "—";
      const zona = [p.direccion_localidad, p.direccion_provincia, p.direccion_cp && `CP ${p.direccion_cp}`].filter(Boolean).join(", ");
      const fmtRef = (label, val) => {
        const s = String(val || "").trim();
        if (!s) return null;
        if (/^(piso|p°|p\.|dpto|dto|depto|departamento|timbre|t°|t\.|ref)/i.test(s)) return s;
        return `${label} ${s}`;
      };
      const refPartes = [
        fmtRef("Piso", p.direccion_piso),
        fmtRef("Dpto", p.direccion_dpto),
        fmtRef("Timbre", p.direccion_timbre),
      ].filter(Boolean);
      const refExtra = refPartes.join(" · ");
      const contacto = [p.cliente_nombre || "—", p.cliente_telefono].filter(Boolean).join("\n");
      const pago = esEfectivo(p) && !isPagado(p) ? fmtMoney(p.total) : "";
      const info = legInfo[i] || {};
      // Llegada: sólo si el usuario cargó horario de salida y hay datos para calcularlo.
      const llegadaCell = info.llegadaMin != null ? minToHHMM(info.llegadaMin) : "—";
      // Tramo: minutos entre la llegada a esta parada y la llegada a la anterior.
      // Para el primer destino, se calcula respecto al horario de salida del reparto.
      // Este cálculo respeta los overrides manuales que hizo el operador.
      const prevLlegadaMin = i > 0
        ? (legInfo[i - 1]?.llegadaMin ?? null)
        : horaSalidaMin;
      const tramoMinCalc = (info.llegadaMin != null && prevLlegadaMin != null)
        ? info.llegadaMin - prevLlegadaMin
        : null;
      const tramoCell = tramoMinCalc != null ? `${Math.round(tramoMinCalc)} min` : "—";
      // Ventana horaria comprometida al cliente (si existe) se muestra debajo de Llegada.
      const ventana = (p.horario_entrega_desde || p.horario_entrega_hasta)
        ? `\n${p.horario_entrega_desde && p.horario_entrega_hasta ? `${p.horario_entrega_desde}–${p.horario_entrega_hasta}` : (p.horario_entrega_desde ? `desde ${p.horario_entrega_desde}` : `hasta ${p.horario_entrega_hasta}`)}`
        : "";
      return [
        String(i + 1), contacto,
        `${dir}${refExtra ? "\n" + refExtra : ""}${zona ? "\n" + zona : ""}`,
        `${llegadaCell}${ventana}`,
        tramoCell,
        pago,
      ];
    });
    autoTable(doc, {
      startY: y,
      margin: { left: 10, right: 10 },
      theme: "grid",
      head: [["#", "Cliente", "Dirección", "Llegada", "Tramo", "Pago"]],
      body,
      styles: { fontSize: 8, cellPadding: 1.6, valign: "top", textColor: [40, 40, 40] },
      headStyles: { fillColor: [146, 26, 14], textColor: [255, 255, 255], fontSize: 8, halign: "center" },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },                    // #
        1: { cellWidth: 65 },                                       // Cliente
        2: { cellWidth: 120 },                                      // Dirección
        3: { cellWidth: 28, halign: "center", fontStyle: "bold" },  // Llegada
        4: { cellWidth: 22, halign: "center" },                     // Tramo
        5: { cellWidth: "auto", halign: "right", fontStyle: "bold" }, // Pago
      },
      didParseCell: (hookData) => {
        // Verde en columna Pago sólo cuando hay monto a cobrar en efectivo
        if (hookData.section === "body" && hookData.column.index === 5) {
          const parada = rows[hookData.row.index].parada;
          if (parada && esEfectivo(parada) && !isPagado(parada)) {
            hookData.cell.styles.fillColor = [220, 250, 220];
            hookData.cell.styles.textColor = [22, 101, 52];
            hookData.cell.styles.fontStyle = "bold";
          }
        }
        // Llegada en azul suave para diferenciar
        if (hookData.section === "body" && hookData.column.index === 3) {
          hookData.cell.styles.textColor = [30, 64, 175];
        }
        if (hookData.section === "body" && hookData.column.index === 4) {
          hookData.cell.styles.textColor = [90, 90, 90];
        }
      },
    });

    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Chofer: ${chofer} · ${fecha}`, 10, 292);
      doc.text(`Página ${i} de ${total}`, pageW - 10, 292, { align: "right" });
    }

    const slug = chofer.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "sin-asignar";
    doc.save(`hoja-reparto-${fecha}-${slug}.pdf`);
  };

  // Genera un PDF individual por VIAJE (batch: uno por cada viaje de cada chofer).
  const exportarPDFPorChofer = async () => {
    if (!data?.grupos?.length) return toast.warning("No hay paradas para exportar en esta fecha.");
    let n = 0;
    // Iteramos secuencialmente para no saturar Nominatim/OSRM
    for (const viaje of viajesList) {
      const horaMin = hhmmToMin(horarioSalidaPorChofer[viaje.key] || "");
      const legs = await fetchRouteEta(viaje.key, viaje.rows);
      renderChoferPDF(viaje.label, viaje.rows, horaMin, legs, viaje.vehiculo || null);
      n += 1;
    }
    if (n === 0) toast.warning("No hay paradas para exportar.");
    else toast.success(`${n} PDF${n > 1 ? "s" : ""} descargado${n > 1 ? "s" : ""} (uno por viaje)`);
  };

  // Descarga el PDF de un único viaje respetando el orden optimizado (si existe).
  // Usa OSRM para obtener tiempos reales de manejo; si falla, cae a estimación.
  const descargarRutaChofer = async (viaje) => {
    if (!viaje || !viaje.rows || viaje.rows.length === 0) return toast.warning(`${viaje?.label || "El chofer"} no tiene paradas asignadas.`);
    const horaMin = hhmmToMin(horarioSalidaPorChofer[viaje.key] || "");
    const legs = await fetchRouteEta(viaje.key, viaje.rows);
    renderChoferPDF(viaje.label, viaje.rows, horaMin, legs, viaje.vehiculo || null);
    toast.success(`Ruta de ${viaje.label} descargada${horaMin != null ? ` · salida ${minToHHMM(horaMin)}` : ""}${legs ? " · tiempos reales" : ""}`);
  };

  const totalizadores = data?.totales || { paradas: 0, grupos: 0, kg: 0, monto: 0 };

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 mb-4">
        <Truck size={30} weight="duotone" className="text-[#921A0E]" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-heading">Hoja de Ruta</h1>
        </div>
        <div className="flex flex-col gap-2 no-print items-end">
          <Button
            onClick={() => setWaConfigOpen(true)}
            variant="outline"
            className="gap-2 h-8 text-[12px] border-green-600 text-green-700 hover:bg-green-50"
            data-testid="btn-editar-mensaje-wa"
            title="Editar plantilla de mensaje de WhatsApp"
          >
            <WhatsappLogo size={14} /> Editar mensaje WhatsApp
          </Button>
          <Button
            onClick={() => setNuevaParadaOpen(true)}
            className="gap-2 bg-[#921A0E] hover:bg-[#7a1409] text-white"
            data-testid="btn-agregar-parada"
          >
            <Plus size={16} /> Agregar parada
          </Button>
        </div>
      </div>

      {/* Controles */}
      <div className="flex flex-wrap gap-3 items-end mb-4 no-print">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500 mb-1">Fecha de envío</div>
          <DateInput value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-44" data-testid="reparto-fecha" />
        </div>
        <div className="flex-1 min-w-[280px] max-w-[560px]">
          <div className="text-xs font-mono uppercase text-neutral-500 mb-1 flex items-center gap-1">
            <MapPin size={11} weight="fill" className="text-[#921A0E]" />
            Origen de la ruta
            {geocodingOrigen && (
              <span className="text-[10px] font-mono normal-case tracking-normal text-neutral-400 ml-2">
                geocodificando…
              </span>
            )}
            {!geocodingOrigen && origenCoords && (
              <span
                className="text-[10px] font-mono normal-case tracking-normal text-emerald-700 ml-2"
                title={`Lat ${origenCoords.lat.toFixed(5)}, Lon ${origenCoords.lon.toFixed(5)} · el algoritmo Optimizar arranca desde acá`}
              >
                ✓ ubicado
              </span>
            )}
            {!geocodingOrigen && !origenCoords && origenRuta.trim() && (
              <span
                className="text-[10px] font-mono normal-case tracking-normal text-amber-600 ml-2"
                title="No se pudieron obtener coordenadas. Optimizar arrancará desde la parada más al NO."
              >
                sin ubicar
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <Input
              type="text"
              value={origenRuta}
              onChange={(e) => setOrigenRuta(e.target.value)}
              placeholder="Dirección de salida (calle, número, localidad, provincia)"
              className="flex-1"
              data-testid="reparto-origen"
            />
            <Button
              variant="outline"
              size="sm"
              className="gap-1 shrink-0"
              onClick={() => {
                if (!origenRuta.trim()) return toast.warning("Cargá una dirección de origen.");
                const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(origenRuta)}`;
                window.open(url, "_blank");
              }}
              title="Ver origen en Google Maps"
              data-testid="reparto-origen-map"
            >
              <MapPin size={14} /> Ver
            </Button>
          </div>
        </div>
      </div>

      {/* Toggle round-trip */}
      <div className="flex items-center gap-2 mb-4 no-print">
      </div>

      {/* Choferes asignados: cada chofer puede tener 1 o más viajes (rutas divididas).
          Vehículo, horario, mapa y PDF se manejan A NIVEL DE VIAJE. Bajo el último viaje
          de cada chofer se despliega el panel de "Ver orden / Dividir ruta". */}
      {viajesList.length > 0 && (
        <div className="mb-5 no-print" data-testid="reparto-choferes-panel">
          <div className="text-xs font-mono uppercase text-neutral-500 mb-2 flex items-center gap-1">
            <User size={12} weight="fill" className="text-[#921A0E]" />
            Choferes asignados ({totalChoferes})
          </div>
          <div className="border border-neutral-300 rounded-lg bg-white divide-y divide-neutral-200 overflow-hidden">
            {viajesList.map((d) => {
              const isExpanded = expandedViaje.has(d.key);
              const splits = splitsPorChofer[d.chofer] || [];
              return (
              <div key={d.key} data-testid={`reparto-chofer-row-${d.key}`}>
                <div className="flex flex-wrap md:flex-nowrap items-center gap-3 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${d.chofer === "Sin asignar" ? "bg-neutral-100 text-neutral-400" : "bg-[#921A0E]/10 text-[#921A0E]"}`}>
                    <User size={16} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {d.chofer}
                      {d.numViajes > 1 && (
                        <Badge className="bg-purple-100 text-purple-800 border-0 text-[9px] px-1.5 py-0 font-mono">
                          VIAJE {d.viajeIdx + 1} / {d.numViajes}
                        </Badge>
                      )}
                      {d.optimizado && d.viajeIdx === 0 && (
                        <Badge className="bg-blue-100 text-blue-800 border-0 text-[9px] px-1.5 py-0">
                          Ruta optimizada
                        </Badge>
                      )}
                      {loadingEtaChofer === d.key && (
                        <span className="text-[9px] font-mono uppercase text-neutral-500">calculando ruta…</span>
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {d.totalParadas} parada{d.totalParadas === 1 ? "" : "s"}
                      <span className="mx-1.5 text-neutral-300">·</span>
                      {d.totalKg.toLocaleString("es-AR")} kg
                      {d.totalACobrar > 0 && (
                        <>
                          <span className="mx-1.5 text-neutral-300">·</span>
                          <span className="text-amber-700 font-medium">a cobrar {fmtMoney(d.totalACobrar)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Vehículo del viaje: se guarda por (chofer × viaje) en localStorage */}
                <div className="flex items-center gap-1 shrink-0">
                  <label
                    className="text-[10px] font-mono uppercase text-neutral-500 whitespace-nowrap flex items-center gap-1"
                    htmlFor={`veh-${d.key}`}
                  >
                    <Truck size={11} weight="fill" className="text-neutral-500" />
                    Vehículo
                  </label>
                  <Input
                    id={`veh-${d.key}`}
                    list="logistica-vehiculos-list"
                    placeholder="Patente"
                    value={vehiculosPorChoferViaje[d.key] || ""}
                    onChange={(e) => setVehiculosPorChoferViaje((s) => ({ ...s, [d.key]: e.target.value }))}
                    className="h-8 w-[110px] text-xs px-2"
                    data-testid={`reparto-chofer-vehiculo-${d.key}`}
                    title="Vehículo asignado a este viaje. Aparece en el PDF y se puede reasignar por viaje."
                  />
                </div>
                {/* Horario de salida (por viaje) — persistido por fecha en localStorage */}
                <div className="flex items-center gap-1 shrink-0">
                  <label
                    className="text-[10px] font-mono uppercase text-neutral-500 whitespace-nowrap flex items-center gap-1"
                    htmlFor={`salida-${d.key}`}
                  >
                    <Clock size={11} weight="fill" className="text-[#921A0E]" />
                    Horario salida
                  </label>
                  <Input
                    id={`salida-${d.key}`}
                    type="time"
                    step="60"
                    value={horarioSalidaPorChofer[d.key] || ""}
                    onChange={(e) => setHorarioSalidaPorChofer((s) => ({ ...s, [d.key]: e.target.value }))}
                    className="h-8 w-[90px] text-xs px-2 font-mono"
                    data-testid={`reparto-chofer-salida-${d.key}`}
                    title="Se usa para calcular las horas de llegada estimadas en el PDF de este viaje"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  {d.chofer !== "Sin asignar" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs h-8"
                      onClick={() => setExpandedViaje((s) => { const n = new Set(s); if (n.has(d.key)) n.delete(d.key); else n.add(d.key); return n; })}
                      title="Ver / reordenar / dividir las paradas de este viaje"
                      data-testid={`reparto-chofer-orden-toggle-${d.key}`}
                    >
                      <ListIcon size={14} /> {isExpanded ? "Ocultar" : "Ver / Dividir"} {isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </Button>
                  )}
                  {d.viajeIdx > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs h-8 border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={() => deshacerCorteViaje(d)}
                      title={`Deshacer el corte y unir este viaje con el Viaje ${d.viajeIdx}`}
                      data-testid={`reparto-chofer-deshacer-corte-${d.key}`}
                    >
                      <ArrowClockwise size={14} weight="bold" /> Deshacer corte
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => abrirRutaChoferMaps(d)}
                    title="Abre la ruta de este viaje en Google Maps. El link se copia al portapapeles para pasarle al chofer."
                    data-testid={`reparto-chofer-maps-${d.key}`}
                  >
                    <MapTrifold size={14} weight="fill" /> Ver en Maps
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs h-8 border-[#921A0E] text-[#921A0E] hover:bg-[#921A0E]/5"
                    onClick={() => descargarRutaChofer(d)}
                    title="Descargar PDF con la ruta completa de este viaje"
                    data-testid={`reparto-chofer-descargar-${d.key}`}
                  >
                    <DownloadSimple size={14} weight="bold" /> Descargar ruta
                  </Button>
                  {d.chofer !== "Sin asignar" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs h-8"
                          title="Optimizar el orden de las paradas del chofer (afecta todos sus viajes)"
                          data-testid={`reparto-chofer-optimizar-${d.key}`}
                        >
                          <PathIconAlias size={14} /> Optimizar ruta <CaretDown size={12} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[11px] font-mono uppercase text-neutral-500">
                          Criterio de optimización
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => optimizarPorChofer(d.chofer, "distancia")}
                          className="cursor-pointer"
                          data-testid={`reparto-chofer-optimizar-distancia-${d.key}`}
                        >
                          <Ruler size={14} className="mr-2 text-[#921A0E]" />
                          <div className="flex-1">
                            <div className="text-sm font-medium">Por distancia (km)</div>
                            <div className="text-[10px] text-neutral-500">Camino más corto entre paradas</div>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => optimizarPorChofer(d.chofer, "tiempo")}
                          className="cursor-pointer"
                          data-testid={`reparto-chofer-optimizar-tiempo-${d.key}`}
                        >
                          <Clock size={14} className="mr-2 text-blue-700" />
                          <div className="flex-1">
                            <div className="text-sm font-medium">Por tiempo de ruta</div>
                            <div className="text-[10px] text-neutral-500">Prioriza ventanas horarias + km</div>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                </div>
                {isExpanded && d.chofer !== "Sin asignar" && (
                  <div className="px-4 pb-3 pt-1 bg-neutral-50/50 border-t border-neutral-200" data-testid={`reparto-chofer-orden-${d.key}`}>
                    <div className="text-[10px] font-mono uppercase text-neutral-500 mb-1.5 flex items-center gap-1">
                      Orden de paradas · Dividir en viajes
                      <span className="text-neutral-400 normal-case tracking-normal">— arrastrá con el mouse o ↑/↓ · ✂ divide en un nuevo viaje.</span>
                    </div>
                    <ol className="space-y-1">
                      {d.rows.map((r, idx) => {
                        const par = r.parada;
                        const dir = [par.direccion_calle, par.direccion_numero].filter(Boolean).join(" ") || par.direccion_libre || "—";
                        const zona = [par.direccion_localidad, par.direccion_provincia].filter(Boolean).join(", ");
                        const esCorte = splits.includes(par.id);
                        const esUltimaDelViaje = idx === d.rows.length - 1;
                        const isDragging = dragParada?.key === d.key && dragParada?.fromIdx === idx;
                        const isDragOver = dragParada?.key === d.key && dragOverIdx === idx && dragParada?.fromIdx !== idx;
                        return (
                          <Fragment key={par.id}>
                            <li
                              draggable
                              onDragStart={(e) => {
                                setDragParada({ key: d.key, fromIdx: idx });
                                try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
                              }}
                              onDragEnd={() => { setDragParada(null); setDragOverIdx(null); }}
                              onDragOver={(e) => {
                                if (dragParada?.key === d.key) {
                                  e.preventDefault();
                                  if (dragOverIdx !== idx) setDragOverIdx(idx);
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (dragParada?.key === d.key && dragParada.fromIdx !== idx) {
                                  reordenarParadaViaje(d, dragParada.fromIdx, idx);
                                }
                                setDragParada(null); setDragOverIdx(null);
                              }}
                              className={`flex items-center gap-2 bg-white border rounded px-2 py-1.5 text-xs transition-all ${
                                isDragging ? "opacity-40 border-[#921A0E]" :
                                isDragOver ? "border-[#921A0E] border-2 shadow-md" :
                                "border-neutral-200"
                              }`}
                              data-testid={`reparto-chofer-orden-item-${d.key}-${idx}`}
                            >
                              <span className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-[#921A0E]" title="Arrastrá para reordenar">
                                <DotsSixVertical size={14} weight="bold" />
                              </span>
                              <span className="w-6 shrink-0 font-mono text-neutral-500 font-bold text-center text-[11px]">#{idx + 1}</span>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{par.cliente_nombre || "—"}</div>
                                <div className="truncate text-[10px] text-neutral-500">
                                  {dir}{zona ? ` · ${zona}` : ""}
                                  {(par.direccion_lat == null || par.direccion_lon == null) && (
                                    <span className="ml-1 text-amber-600">· sin coord</span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => moverParadaViaje(d, idx, -1)}
                                disabled={idx === 0}
                                className="h-6 w-6 flex items-center justify-center rounded border border-neutral-300 text-neutral-600 hover:bg-[#921A0E]/5 hover:text-[#921A0E] disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Subir un lugar dentro de este viaje"
                                data-testid={`reparto-chofer-orden-up-${d.key}-${idx}`}
                              >
                                <ArrowUp size={12} weight="bold" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moverParadaViaje(d, idx, 1)}
                                disabled={idx === d.rows.length - 1}
                                className="h-6 w-6 flex items-center justify-center rounded border border-neutral-300 text-neutral-600 hover:bg-[#921A0E]/5 hover:text-[#921A0E] disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Bajar un lugar dentro de este viaje"
                                data-testid={`reparto-chofer-orden-down-${d.key}-${idx}`}
                              >
                                <ArrowDown size={12} weight="bold" />
                              </button>
                            </li>
                            {/* "Cortar aquí" — SOLO si no es la última parada del viaje (dentro
                                del viaje). Si se marca, este viaje se corta en 2 y aparece un
                                nuevo Viaje N+1 abajo con todas las paradas que siguen. */}
                            {!esUltimaDelViaje && !esCorte && (
                              <li className="flex justify-center px-2">
                                <button
                                  type="button"
                                  onClick={() => toggleCorteEnParada(d.chofer, par.id)}
                                  className="text-[9px] font-mono uppercase text-neutral-400 hover:text-purple-700 border border-dashed border-transparent hover:border-purple-300 rounded px-2 py-0.5"
                                  title="Cortar la ruta acá — todo lo que sigue pasa a ser un nuevo viaje"
                                  data-testid={`reparto-chofer-cortar-${d.key}-${idx}`}
                                >
                                  ✂ cortar aquí (nuevo viaje)
                                </button>
                              </li>
                            )}
                          </Fragment>
                        );
                      })}
                    </ol>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Totales */}

      {/* Print header */}
      <div className="print-only mb-4">
        <div className="text-lg font-bold">Hoja de Reparto · {fecha}</div>
        {origenRuta && (
          <div className="text-xs text-neutral-700 mt-0.5">
            <strong>Salida:</strong> {origenRuta}
          </div>
        )}
        <div className="text-xs text-neutral-600">
          {totalizadores.paradas} paradas · {totalizadores.grupos} {groupLabel.toLowerCase()} · {totalizadores.kg} kg
        </div>
      </div>

      {loading && <div className="text-neutral-500 text-sm py-6">Cargando reparto…</div>}
      {!loading && (!data?.grupos || data.grupos.length === 0) && (
        <div className="border border-dashed border-neutral-300 bg-neutral-50 rounded-lg p-10 text-center">
          <Truck size={40} weight="thin" className="mx-auto mb-3 text-neutral-400" />
          <div className="text-neutral-700 font-medium">No hay pedidos con envío para el {fecha}</div>
          <div className="text-xs text-neutral-500 mt-1">
            Verificá la fecha de envío en Tienda Nube / Pedidos y que estén en un estado activo.
          </div>
        </div>
      )}

      <div className="border border-neutral-300 rounded-lg bg-white overflow-hidden divide-y divide-neutral-200" data-testid="reparto-paradas-list">
        {(() => {
          const grupos = data?.grupos || [];
          // Aplanado global: numeración 1..N cruzando zonas, preservando asignación por grupo.
          let flat = grupos.flatMap((g) => paradasOrdenadas(g).map((p) => ({ p, g })));
          // Aplicar reordenamiento manual por chofer: si el usuario reordenó los pedidos
          // de un chofer arriba (drag/↑/↓), reflejarlo en este listado.
          Object.entries(ordenPorChofer || {}).forEach(([chofer, ids]) => {
            if (!ids || ids.length === 0) return;
            const posiciones = [];
            const items = [];
            flat.forEach((row, i) => {
              const asig = editAsignacion[row.g.clave] || {};
              const choferEfectivo = (row.p.chofer_individual || asig.chofer || "").trim();
              if (choferEfectivo === chofer) {
                posiciones.push(i);
                items.push(row);
              }
            });
            if (items.length < 2) return;
            const byId = new Map(items.map((it) => [it.p.id, it]));
            const reordenados = ids.map((id) => byId.get(id)).filter(Boolean);
            byId.forEach((it) => { if (!ids.includes(it.p.id)) reordenados.push(it); });
            const next = [...flat];
            posiciones.forEach((pos, i) => { next[pos] = reordenados[i]; });
            flat = next;
          });
          return flat.map(({ p, g }, idx) => {
            const asig = editAsignacion[g.clave] || { chofer: "", vehiculo: "" };
            return (
              <ParadaRow
                key={p.id}
                idx={idx + 1}
                p={p}
                grupoChofer={asig.chofer}
                llegadaHHMM={llegadasPorParada[p.id]}
                llegadaOverride={llegadaOverridePorParada[p.id]}
                onOverrideLlegada={(hhmm) => {
                  // Actualización local optimista.
                  setLlegadaOverridePorParada((prev) => {
                    const next = { ...prev };
                    if (!hhmm) delete next[p.id];
                    else next[p.id] = hhmm;
                    return next;
                  });
                  // Persist en backend (compartido entre operadores).
                  api.put("/logistica/llegada-overrides", {
                    fecha,
                    parada_id: p.id,
                    hhmm: hhmm || "",
                  }).catch(() => {
                    toast.error("No se pudo guardar el horario en el servidor. Se mantiene local.");
                  });
                }}
                onGuardarAsignacion={guardarParadaAsignacion}
                onAbrirRuta={() => abrirRutaParada(p)}
                onEnviarWhatsapp={() => {
                  const asig = editAsignacion[g.clave] || {};
                  const choferGrupo = asig.chofer || "";
                  enviarWhatsappParada(p, p.chofer_individual || choferGrupo);
                }}
                onDeleteExtra={p.origen === "extra" ? () => setDeleteParadaExtraId(p.id) : null}
              />
            );
          });
        })()}
      </div>

      {/* Datalist compartidos: choferes y vehículos desde REC */}
      <datalist id="logistica-choferes-list">
        {transportistas.map((t) => (
          <option key={t.id} value={t.nombre}>
            {t.tipo === "propio" ? "Propio" : "Tercero"}{t.cuit ? ` · CUIT ${t.cuit}` : ""}{t.telefono ? ` · ${t.telefono}` : ""}
          </option>
        ))}
      </datalist>
      <datalist id="logistica-vehiculos-list">
        {vehiculos.map((v) => (
          <option key={v.id} value={v.dominio}>
            {[v.marca_modelo, v.transportista_nombre, v.dominio_acoplado && `Acoplado ${v.dominio_acoplado}`].filter(Boolean).join(" · ")}
          </option>
        ))}
      </datalist>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-break-inside-avoid { break-inside: avoid; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Dialog: Configuración del mensaje de WhatsApp */}
      <Dialog open={waConfigOpen} onOpenChange={setWaConfigOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-wa-config">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsappLogo size={20} className="text-green-600" />
              Plantilla del mensaje de WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[520px] overflow-y-auto">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Encabezado (opcional)</Label>
              <Input
                value={waConfig.encabezado}
                onChange={(e) => setWaConfig((p) => ({ ...p, encabezado: e.target.value }))}
                placeholder="Ej: Hola! Te paso el próximo pedido:"
                className="h-9 rounded-sm"
                data-testid="wa-cfg-encabezado"
              />
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2 block">
                Campos a incluir
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["nombre_cliente", "Nombre del cliente"],
                  ["num_pedido", "N° de pedido"],
                  ["direccion", "Dirección de entrega"],
                  ["telefono_cliente", "Teléfono del cliente"],
                  ["especificacion", "Especificación de productos"],
                  ["hora_estimada", "Hora estimada (ETA)"],
                  ["observaciones", "Observaciones del pedido"],
                  ["estado_pago", "Estado de pago (pago / a cobrar)"],
                  ["monto_a_cobrar", "Mostrar monto a cobrar"],
                  ["link_maps", "Link a Google Maps"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-[13px] select-none border border-neutral-200 rounded-sm px-2 py-1.5 hover:bg-neutral-50">
                    <input
                      type="checkbox"
                      checked={!!waConfig.campos[key]}
                      onChange={(e) => setWaConfig((p) => ({ ...p, campos: { ...p.campos, [key]: e.target.checked } }))}
                      className="h-4 w-4"
                      data-testid={`wa-cfg-${key}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Pie / firma (opcional)</Label>
              <Input
                value={waConfig.pie}
                onChange={(e) => setWaConfig((p) => ({ ...p, pie: e.target.value }))}
                placeholder="Ej: Gracias! · La Bife SRL"
                className="h-9 rounded-sm"
                data-testid="wa-cfg-pie"
              />
            </div>

            {/* Vista previa */}
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Vista previa</Label>
              <div className="bg-[#DCF8C6] p-3 rounded-md text-[12px] font-mono whitespace-pre-wrap border border-green-200">
                {armarMensajeParada({
                  cliente_nombre: "Federico Bolán",
                  cliente_telefono: "1140645013",
                  numero: 123,
                  direccion_calle: "Murguiondo",
                  direccion_numero: "161",
                  direccion_localidad: "CABA",
                  direccion_provincia: "Buenos Aires",
                  total: 26899.02,
                  pago_metodo: "cta_cte",
                  observaciones: "Dejar en encargado",
                  items: [{ especificacion: "Cortar en trozos" }],
                  hora_estimada: "17:30",
                }) || "(mensaje vacío)"}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaConfigOpen(false)}>Cancelar</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={async () => {
                try {
                  const { data } = await api.put("/logistica/config/wa-mensaje", waConfig);
                  setWaConfig(data);
                  toast.success("Plantilla guardada");
                  setWaConfigOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "No se pudo guardar");
                }
              }}
              data-testid="wa-cfg-save"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Elegir chofer para enviar WhatsApp */}
      <Dialog open={!!waPicker} onOpenChange={(v) => !v && setWaPicker(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-wa-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsappLogo size={20} className="text-green-600" />
              Elegir destinatario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[380px] overflow-y-auto">
            <div className="text-[12px] text-neutral-600 mb-1">
              La parada no tiene un chofer asignado (o el asignado no tiene teléfono cargado). Elegí a quién mandarle:
            </div>
            {(transportistas || [])
              .filter((t) => normalizarTelefonoWa(t.telefono))
              .map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    const tel = normalizarTelefonoWa(t.telefono);
                    const url = `https://wa.me/${tel}?text=${encodeURIComponent(waPicker.mensaje)}`;
                    window.open(url, "_blank");
                    setWaPicker(null);
                  }}
                  className="w-full flex items-center justify-between gap-3 border border-neutral-200 hover:border-green-500 hover:bg-green-50 rounded-sm px-3 py-2 text-left"
                  data-testid={`wa-picker-${t.id}`}
                >
                  <div>
                    <div className="font-semibold text-[13px]">{t.nombre}</div>
                    <div className="text-[11px] font-mono text-neutral-500">{t.telefono}</div>
                  </div>
                  <WhatsappLogo size={18} className="text-green-600" />
                </button>
              ))
            }
            {(transportistas || []).filter((t) => normalizarTelefonoWa(t.telefono)).length === 0 && (
              <div className="text-center text-neutral-400 text-[12px] py-6">
                Ningún transportista tiene teléfono cargado. Cargalo en el módulo REC.
              </div>
            )}
            <button
              onClick={() => {
                const url = `https://wa.me/?text=${encodeURIComponent(waPicker.mensaje)}`;
                window.open(url, "_blank");
                setWaPicker(null);
              }}
              className="w-full flex items-center gap-3 border border-dashed border-neutral-300 hover:border-neutral-500 rounded-sm px-3 py-2 text-left text-neutral-600"
              data-testid="wa-picker-otro"
            >
              <WhatsappLogo size={18} />
              <span className="text-[13px]">Otro contacto… (abrir picker de WhatsApp)</span>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaPicker(null)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nueva parada extra */}
      <Dialog open={nuevaParadaOpen} onOpenChange={setNuevaParadaOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-nueva-parada">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={20} className="text-[#921A0E]" />
              Agregar parada extra
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Descripción *</Label>
              <Input
                value={nuevaParada.descripcion}
                onChange={(e) => setNuevaParada((f) => ({ ...f, descripcion: e.target.value }))}
                placeholder="Ej: Retirar cobro en efectivo · Pasar por proveedor · Buscar packaging"
                data-testid="nueva-parada-descripcion"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-500 flex items-center gap-1">
                <MapPin size={12} /> Dirección
              </Label>
              <AddressAutocomplete
                value={nuevaParada.direccion_libre || [nuevaParada.direccion_calle, nuevaParada.direccion_numero].filter(Boolean).join(" ")}
                onChange={(v) => setNuevaParada((f) => ({ ...f, direccion_libre: v }))}
                onSelect={(place) => {
                  const latRaw = place.lat ?? place.direccion_lat ?? null;
                  const lonRaw = place.lon ?? place.direccion_lon ?? null;
                  setNuevaParada((f) => ({
                    ...f,
                    direccion_calle: place.direccion_calle || "",
                    direccion_numero: place.direccion_numero || "",
                    direccion_localidad: place.direccion_localidad || "",
                    direccion_provincia: place.direccion_provincia || "",
                    direccion_cp: place.direccion_cp || "",
                    direccion_lat: latRaw != null && latRaw !== "" ? Number(latRaw) : null,
                    direccion_lon: lonRaw != null && lonRaw !== "" ? Number(lonRaw) : null,
                    direccion_libre: [place.direccion_calle, place.direccion_numero].filter(Boolean).join(" "),
                  }));
                }}
                placeholder="Buscá la dirección (calle, número, localidad)…"
                testId="nueva-parada-direccion-autocomplete"
              />
              {nuevaParada.direccion_localidad && (
                <div className="text-[10px] text-neutral-500 font-mono">
                  {nuevaParada.direccion_localidad}
                  {nuevaParada.direccion_provincia && ` · ${nuevaParada.direccion_provincia}`}
                  {nuevaParada.direccion_cp && ` · CP ${nuevaParada.direccion_cp}`}
                  {(nuevaParada.direccion_lat != null && nuevaParada.direccion_lon != null) && ` · ${Number(nuevaParada.direccion_lat).toFixed(4)}, ${Number(nuevaParada.direccion_lon).toFixed(4)}`}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Monto a cobrar</Label>
                <NumberInput
                  step="0.01"
                  min="0"
                  value={nuevaParada.monto}
                  onChange={(e) => setNuevaParada((f) => ({ ...f, monto: e.target.value }))}
                  placeholder="0.00"
                  data-testid="nueva-parada-monto"
                />
                <div className="text-[10px] text-neutral-500">Dejalo en 0 si sólo es una gestión sin cobro.</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Teléfono (opc.)</Label>
                <Input
                  value={nuevaParada.telefono}
                  onChange={(e) => setNuevaParada((f) => ({ ...f, telefono: e.target.value }))}
                  placeholder="Contacto en el lugar"
                  data-testid="nueva-parada-telefono"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Fecha de la parada</Label>
              <DateInput
                value={nuevaParada.fecha_parada || fecha}
                min={fecha}
                onChange={(e) => setNuevaParada((f) => ({ ...f, fecha_parada: e.target.value }))}
                data-testid="nueva-parada-fecha"
              />
              <div className="text-[10px] text-neutral-500">
                Por defecto, la fecha visible en la hoja ({fecha}). Cambiala si querés dejar la parada agendada para otro día.
              </div>
            </div>

            <div className="rounded-sm border bg-amber-50/50 border-amber-200 p-2 text-[11px] text-amber-800 font-mono">
              La parada aparecerá el <b>{nuevaParada.fecha_parada || fecha}</b> en la hoja de reparto como <b>EX#N</b>. Podés asignarle chofer/vehículo como a cualquier pedido y eliminarla si te confundís.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevaParadaOpen(false)} disabled={savingParada}>
              Cancelar
            </Button>
            <Button
              onClick={guardarNuevaParada}
              disabled={savingParada}
              className="bg-[#921A0E] hover:bg-[#7a1409] text-white"
              data-testid="nueva-parada-guardar"
            >
              {savingParada ? "Guardando…" : "Agregar parada"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar parada extra */}
      <AlertDialog open={!!deleteParadaExtraId} onOpenChange={(o) => { if (!o) setDeleteParadaExtraId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta parada extra?</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitará de la hoja de reparto de forma definitiva. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarEliminarParadaExtra} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ParadaRow({ idx, p, grupoChofer, llegadaHHMM, llegadaOverride, onOverrideLlegada, onGuardarAsignacion, onAbrirRuta, onEnviarWhatsapp, onDeleteExtra }) {
  const dir = [p.direccion_calle, p.direccion_numero].filter(Boolean).join(" ") || p.direccion_libre || "—";
  const refPartes = [];
  const fmtRef = (label, val) => {
    const s = String(val || "").trim();
    if (!s) return null;
    // Si el usuario ya escribió "TIMBRE 2" / "Piso 3 B" en el mismo campo, no le
    // prefijamos el label para no leer "Piso TIMBRE 2".
    if (/^(piso|p°|p\.|dpto|dto|depto|departamento|timbre|t°|t\.|ref)/i.test(s)) return s;
    return `${label} ${s}`;
  };
  const pisoTxt = fmtRef("Piso", p.direccion_piso);
  const dptoTxt = fmtRef("Dpto", p.direccion_dpto);
  const timbreTxt = fmtRef("Timbre", p.direccion_timbre);
  if (pisoTxt) refPartes.push(pisoTxt);
  if (dptoTxt) refPartes.push(dptoTxt);
  if (timbreTxt) refPartes.push(timbreTxt);
  const refExtra = refPartes.join(" · ");
  const mapQ = (p.direccion_lat && p.direccion_lon)
    ? `${p.direccion_lat},${p.direccion_lon}`
    : `${dir}, ${p.direccion_localidad || ""}`.trim();
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQ)}`;
  const chofer = p.chofer_individual ?? "";
  const usaGrupoChofer = !chofer && grupoChofer;
  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-start text-sm hover:bg-neutral-50/50 transition-colors">
      <div className="col-span-1">
        <div className="font-mono text-neutral-500 font-bold">#{idx}</div>
        {llegadaHHMM && (
          <div
            className="mt-0.5 flex items-center gap-0.5"
            title="Hora estimada de llegada — clic para forzar el horario y recalcular los siguientes"
            data-testid={`parada-llegada-${p.id}`}
          >
            <Clock size={10} weight="fill" className={llegadaOverride ? "text-amber-700" : "text-blue-700"} />
            <input
              type="time"
              value={llegadaOverride || llegadaHHMM}
              onChange={(e) => onOverrideLlegada && onOverrideLlegada(e.target.value)}
              onBlur={(e) => {
                // Si el usuario dejó el valor igual al estimado y no había override, no guardar.
                if (!llegadaOverride && e.target.value === llegadaHHMM) return;
              }}
              className={`w-[86px] px-1 py-0 text-[11px] font-mono border rounded-sm bg-white ${
                llegadaOverride
                  ? "border-amber-300 text-amber-800 font-semibold"
                  : "border-transparent text-blue-700 hover:border-neutral-300"
              }`}
              data-testid={`parada-llegada-input-${p.id}`}
            />
            {llegadaOverride && (
              <button
                type="button"
                onClick={() => onOverrideLlegada && onOverrideLlegada("")}
                title="Volver al horario estimado"
                data-testid={`parada-llegada-reset-${p.id}`}
                className="text-[10px] text-amber-700 hover:text-amber-900 leading-none"
              >
                ✕
              </button>
            )}
          </div>
        )}
      </div>
      <div className="col-span-3 min-w-0">
        <div className="font-medium truncate flex items-center gap-1">
          <User size={12} className="text-neutral-400 shrink-0" />
          {p.cliente_nombre || "—"}
        </div>
        {p.cliente_telefono && (
          <a href={`tel:${p.cliente_telefono}`} className="text-[11px] text-neutral-500 flex items-center gap-1 hover:text-[#921A0E]">
            <Phone size={11} /> {p.cliente_telefono}
          </a>
        )}
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          <Badge className={`${ESTADO_COLORS[p.estado] || "bg-neutral-100"} border-0 text-[10px] px-1.5 py-0`}>
            {ESTADO_LABEL[p.estado] || p.estado}
          </Badge>
          <span className="text-[10px] text-neutral-400 font-mono">{p.numero}</span>
          {(p.horario_entrega_desde || p.horario_entrega_hasta) && (
            <Badge
              className="bg-amber-100 text-amber-900 border-amber-300 border text-[10px] px-1.5 py-0 font-mono"
              title="Rango horario solicitado por el cliente"
              data-testid={`ruta-horario-${p.id}`}
            >
              🕐 {p.horario_entrega_desde && p.horario_entrega_hasta
                ? `${p.horario_entrega_desde}–${p.horario_entrega_hasta}`
                : p.horario_entrega_desde
                ? `desde ${p.horario_entrega_desde}`
                : `hasta ${p.horario_entrega_hasta}`}
            </Badge>
          )}
        </div>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="flex items-start gap-1">
          <MapPin size={12} className="text-neutral-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <a
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              title={dir}
              className="font-medium hover:underline text-[#921A0E] block leading-tight break-words line-clamp-2"
            >
              {dir}
            </a>
            {refExtra && (
              <div
                className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 py-[1px] mt-0.5 inline-block font-medium max-w-full break-words"
                data-testid={`parada-ref-${p.id}`}
              >
                {refExtra}
              </div>
            )}
            <div
              className="text-[11px] text-neutral-500 leading-tight break-words line-clamp-2"
              title={[p.direccion_localidad, p.direccion_provincia, p.direccion_cp && `CP ${p.direccion_cp}`].filter(Boolean).join(" · ")}
            >
              {[p.direccion_localidad, p.direccion_provincia, p.direccion_cp && `CP ${p.direccion_cp}`].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
        </div>
      </div>
      <div className="col-span-2 min-w-0 no-print">
        <div className="text-[10px] uppercase text-neutral-400 font-mono mb-0.5">Asignación</div>
        <Input
          placeholder={usaGrupoChofer ? `${grupoChofer} (zona)` : "Chofer"}
          list="logistica-choferes-list"
          defaultValue={chofer}
          onBlur={(e) => {
            const nuevo = e.target.value.trim();
            if (nuevo !== (chofer || "")) {
              onGuardarAsignacion(p.id, { chofer_individual: nuevo || null });
            }
          }}
          className={`h-7 text-[11px] ${usaGrupoChofer ? "italic text-neutral-500 placeholder:text-neutral-500" : ""}`}
          data-testid={`parada-chofer-${p.id}`}
        />
      </div>
      <div className="col-span-1 text-right">
        {isPagado(p) ? (
          <>
            {/* Ya pagado: no mostramos nada */}
          </>
        ) : esEfectivo(p) ? (
          <>
            <div
              className="text-sm font-semibold text-amber-900"
              title={p.total_source === "factura" ? `Total tomado de Factura ${p.factura_numero || ""}` : "Cobrar en efectivo al entregar"}
            >
              {fmtMoney(p.total)}
              {p.total_source === "factura" && (
                <span
                  className="ml-1 text-[9px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-[1px] align-middle"
                  data-testid={`parada-total-src-fac-${p.id}`}
                >
                  fac
                </span>
              )}
            </div>
            <Badge className={`${PAGO_COLORS.pending} border text-[9px] px-1.5 py-0 mt-0.5 font-mono`}>
              {PAGO_LABEL.pending}
            </Badge>
            {p.entrega_costo > 0 && <div className="text-[10px] text-neutral-400">envío {fmtMoney(p.entrega_costo)}</div>}
          </>
        ) : (
          <>
            {/* Sin cobro por parte del chofer: no mostramos nada */}
          </>
        )}
      </div>
      <div className="col-span-2 flex flex-col items-end gap-1 no-print">
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-[11px] h-7 w-full max-w-[140px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          onClick={onEnviarWhatsapp}
          title="Compartir esta parada por WhatsApp"
          data-testid={`parada-whatsapp-${p.id}`}
        >
          <WhatsappLogo size={13} weight="fill" /> WhatsApp
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-[11px] h-7 w-full max-w-[140px] border-blue-300 text-blue-700 hover:bg-blue-50"
          onClick={onAbrirRuta}
          title="Abrir la ruta a esta parada en Google Maps"
          data-testid={`parada-ruta-${p.id}`}
        >
          <MapPin size={13} /> Ruta completa
        </Button>
        {onDeleteExtra && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-[11px] h-7 w-full max-w-[140px] border-red-300 text-red-700 hover:bg-red-50"
            onClick={onDeleteExtra}
            title="Eliminar esta parada extra"
            data-testid={`parada-delete-extra-${p.id}`}
          >
            <Trash size={13} /> Eliminar
          </Button>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-neutral-800", testid }) {
  return (
    <div className="border border-neutral-300 rounded-lg p-3 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" className={color} />
        <div className="text-xs uppercase font-mono text-neutral-500">{label}</div>
      </div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
